import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { friendlyErrorMessage } from '../components/ErrorMessage';
import ApprovalWorkflowManager from '../components/ApprovalWorkflowManager';
import CollapsibleSection from '../components/CollapsibleSection';
import { formatCurrency } from '../utils/formatCurrency';
import ScrollableTableWrapper from '../components/ScrollableTableWrapper';
import useResizableColumns from '../hooks/useResizableColumns';
import ResizableHeader from '../components/ResizableHeader';
import ColumnCustomizer, { useColumnVisibility } from '../components/ColumnCustomizer';
import { useDepartment } from '../contexts/DepartmentContext';
import { apiFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { BTN, BTN_XS } from '../constants/buttons';
import { ROLE_LABELS } from '../constants/statuses';
import PeopleManager from '../components/PeopleManager';
import ConfirmDialog from '../components/ConfirmDialog';
import AddPersonPanel from '../components/AddPersonPanel';
import MemberInviteButton from '../components/MemberInviteButton';
import { UserStatusIndicator } from '../components/UserAutocomplete';
import SupplierOptionList from '../components/SupplierOptionList';
import { buildSupplierOptions, matchSupplierOptions } from '../utils/supplierOptions';
import { FIELD_LABEL_CLASS, FORM_INPUT_FIELD, CONTROL_HEIGHT } from '../constants/inputs';
import { EXPANDED_ROW_FRAME } from '../components/TableRowExpansion';
import { TIMESHEET_AGREEMENT_OPTIONS, agreementFamilyValue } from '../utils/timesheetDisplay';
import AgreementSummary, { agreementOverridesPayload } from '../components/timesheets/AgreementSummary';
import { LAST_MANAGER_REASON, lastManagerBlocksRole } from '../constants/lastManager';
import { useIsMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';
// The house expanded-row detail panel (components/ui/CLAUDE.md, Expandable Table Rows) and the
// form field's textarea shape (FORM_INPUT_FIELD without its fixed control height).
const EXPANDED_DETAIL_PANEL = 'bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-600';
const FORM_TEXTAREA_FIELD = `${FORM_INPUT_FIELD.replace(CONTROL_HEIGHT, '')} py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`;

// The Departments section's member-role options, in the order both member
// selects (phone + desktop) have always listed them.
const MEMBER_ROLE_OPTIONS = [
  ['user', 'User'],
  ['buyer', 'Buyer'],
  ['supervisor', 'Supervisor'],
  ['sub_department_lead', 'Sub Dept Lead'],
];

function AdminSettings() {
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPersonalProductions, setShowPersonalProductions] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // 🔻 NO `include_vat` KEY SINCE 2026-08-08. The VAT display was removed from
  // the UI by product decision (reason in `hooks/useVatMode.js`: one boolean
  // cannot express VAT rates that vary per line item). The COLUMN and the API
  // still exist, so this form deliberately does not carry the field at all —
  // see the submit handler for why omitting it is what PRESERVES stored values.
  const [formData, setFormData] = useState({
    company_name: '',
    production_name: '',
    address: '',
  });
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // 'warning' only for a "done, but act on this" outcome from the + Add person box.
  const [successTone, setSuccessTone] = useState('success');

  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [deletingSupplier, setDeletingSupplier] = useState(null);
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [supplierFormData, setSupplierFormData] = useState({
    name: '', contact_name: '', phone: '', email: '', website: '', account_number: ''
  });
  const [expandedSupplierId, setExpandedSupplierId] = useState(null);
  // Supplier names typed on PAST APPROVED ORDERS. Product owner, 2026-08-08:
  // *"When the user starts to add a new supplier we give them the same auto
  // complete drop down."* Same two-tier list as the order form, same component.
  // 🚨 Picking one only fills the NAME BOX; it creates nothing. This screen's
  // Save button is still the only thing that writes a `suppliers` row, and
  // `POST /api/suppliers` is still gated on `canManageDepartmentSettings`.
  const [supplierNameSuggestions, setSupplierNameSuggestions] = useState([]);
  const [supplierNameListOpen, setSupplierNameListOpen] = useState(false);
  const supplierNameRef = useRef(null);

  // 🔻 THE INVITATIONS LIST, ITS INVITE FORM AND THEIR HANDLERS ARE DELETED
  // (2026-09-23, P6 of the join-a-second-department feature; pre-deletion SHA
  // `fd1271b7`). The section's JSX was removed long before — see the note where
  // the Users section renders — so the state, a `GET /api/invitations` fired on
  // every page load, a name-keyed `GET /api/dashboard/user-productions`, two
  // effects and three handlers had no reader. Inviting lives in
  // `components/PeopleManager.js` (the one "+ Add person" form, pending department invites).
  // The absence is pinned by `AdminSettings.noInvitationsList.test.js`.

  // `localStorage` stores everything as TEXT while the API sends `created_by_user_id`
  // as a NUMBER, so this is coerced ONCE here and compared numerically below — a bare
  // `===` across that seam is always false and would empty the create form's
  // production list for everybody (the same string/number seam `utils/orderStepInfo.js`
  // documents). `|| null` rather than `|| 0`: `Number(null)` and `Number('')` are both
  // `0`, and 0 is not a user id.
  const currentUserId = Number(localStorage.getItem('sfx_user_id')) || null;
  const {
    isAdmin: isCurrentUserAdmin,
    activeDepartment,
    capabilities,
    capabilitiesFor,
    holdsDepartmentSettingsSomewhere,
  } = useDepartment();
  // Per-department, from the ACTIVE department's assignment row. Switching the
  // department dropdown switches this, which is the whole point: authority never
  // carries between departments (CLAUDE.md, "Authority model").
  const canManageDeptSettings = !!capabilities?.canManageDepartmentSettings;
  // The roles surface is governed by the OTHER half of the Part B split —
  // `can_assign_department_roles`, the same column the server's role routes
  // resolve. Two capabilities, not one, so a future role granted department
  // settings without role authoring gets exactly that.
  const canAssignDeptRoles = !!capabilities?.canAssignDepartmentRoles;
  // May the viewer CHANGE a member's role in THAT department? The server's
  // question (POL2 fix round 1, B-F4): `PUT /api/departments/:id/members/
  // :userId` runs `mayGrantDepartmentRole` on a role change, which requires
  // `canAssignDepartmentRoles` in that department — not the ACTIVE one, and
  // not the settings capability. The Departments tab lists every department
  // the viewer sits in, so an ungated select offered a change that 403s.
  // Twin of `PeopleManager.js#canChangeSeatRole`.
  const canChangeMemberRole = (deptId) => !!isCurrentUserAdmin
    || capabilitiesFor(deptId)?.canAssignDepartmentRoles === true;

  // --- Page-level tabs (desktop-only trial, mirrors /budget/manage-budgets) ---
  //
  // These flags gate BOTH the desktop tab strip AND the section renders below,
  // via `visibleTabIds`. They are not a cosmetic tab filter — see the comment on
  // `visibleTabIds` for why that distinction cost us seven ungated sections.
  //
  // THE SEAM (product owner, 2026-08-04):
  //
  //   > "Buyers and supervisors need to be able to adjust access rights, backup
  //   > and restore etc for their departments. Buyer and supervisor must not be
  //   > allowed to promote a user within their department above buyer or
  //   > supervisor to admin. Buyer and supervisor should have pretty much full
  //   > control over their department just not site wide admin access."
  //
  // So the split is NOT "which tabs look site-shaped" — it is DEPARTMENT-SCOPED
  // EVERYTHING vs SITE-WIDE ADMIN. That ruling supersedes the previous §4.2 tab
  // split and the §4.4 "export only" decision, and it is what moved Roles &
  // Access Rights, Backup & Restore and Productions off `adminOnly`.
  //
  //   adminOnly    — a SITE-WIDE resource with no department dimension at all,
  //                  so there is nothing to scope (CLAUDE.md: "Resources with no
  //                  department are ADMIN-ONLY"). Equipment Categories is the
  //                  only one left: one catalogue, shared by every department.
  //   deptSettings — administering THIS department: admin, or anyone holding
  //                  the per-department capability (a buyer or supervisor today,
  //                  a future Coordinator role without editing this file). The
  //                  unscoped `isBuyer` disjunct is DELETED (2026-09-23): no route
  //                  behind these sections honours it any more.
  //
  // Whole-instance backup is NOT re-opened by moving the Backup tab: it stays
  // admin-only by construction inside `BackupRestore` (`{isAdmin && …}` on the
  // Full System section, and a non-admin's department id comes from their own
  // active assignment), and the server's `POST /api/backup/restore` is
  // `req.isGlobalAdmin` only.
  //
  // 🔑 `settings-productions` CARRIES A THIRD KEY, `anyEditableProduction`, AND
  // IT IS NOT A TIDY-AWAY (2026-08-10). A production LABEL is not owned by a
  // department, so "may I administer the department I am standing in" is simply
  // the wrong question about it — which is how a supervisor who had created his
  // own label came to be told he needed to be an admin to rename it. The right
  // question is the one the server answers per row and reports as `can_edit`;
  // this section appears when the answer is yes for at least one row, whatever
  // the caller's department capabilities are. `productions` is fetched
  // unconditionally on mount, so the section appears as soon as the list lands.
  const anyEditableProduction = useMemo(
    () => productions.some(p => p.can_edit === true),
    [productions]
  );
  const SECTION_TABS = useMemo(() => {
    const all = [
      { id: 'settings-productions', label: 'Productions', deptSettings: true, anyEditableProduction: true },
      { id: 'settings-departments', label: 'Departments', deptSettings: true },
      { id: 'settings-sub-departments', label: 'Sub-Departments', deptSettings: true },
      { id: 'settings-users', label: 'Users', deptSettings: true },
      { id: 'settings-workflows', label: 'Approval Workflows', deptSettings: true },
      { id: 'settings-suppliers', label: 'Suppliers', deptSettings: true },
      { id: 'settings-equipment-categories', label: 'Equipment Categories', adminOnly: true },
      { id: 'settings-roles', label: 'Roles & Access Rights', deptSettings: true },
      { id: 'settings-backup', label: 'Backup & Restore', deptSettings: true },
    ];
    return all.filter(t => {
      if (t.adminOnly) return isCurrentUserAdmin;
      if (t.anyEditableProduction && anyEditableProduction) return true;
      if (t.deptSettings) return isCurrentUserAdmin || canManageDeptSettings;
      return true;
    });
  }, [isCurrentUserAdmin, canManageDeptSettings, anyEditableProduction]);
  const tabIds = useMemo(() => SECTION_TABS.map(t => t.id), [SECTION_TABS]);
  // ONE source of truth for the desktop tab strip AND every section render.
  //
  // The tab strip alone gated NOTHING on a phone: each CollapsibleSection used
  // to render unconditionally and was hidden with `md:hidden`, a DESKTOP-only
  // rule, so seven of the nine sections were in the DOM and expanded for every
  // caller — including a plain `user`. Derived from `tabIds` rather than
  // recomputed, so the strip and the sections cannot drift apart.
  //
  // The `md:hidden` classes below are UNCHANGED and still correct: they are the
  // desktop tab behaviour. They simply were never a permission gate.
  const visibleTabIds = useMemo(() => new Set(tabIds), [tabIds]);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const allIds = [
        'settings-productions', 'settings-departments', 'settings-sub-departments',
        'settings-suppliers', 'settings-users', 'settings-roles', 'settings-workflows',
        'settings-equipment-categories', 'settings-backup',
      ];
      const hash = window.location.hash.replace(/^#/, '');
      if (allIds.includes(hash)) return hash;
      const stored = localStorage.getItem('sfx_admin_settings_active_tab');
      if (allIds.includes(stored)) return stored;
    } catch {}
    return 'settings-productions';
  });
  // If activeTab points to an admin-only section the current user can't see, fall back
  useEffect(() => {
    if (!tabIds.includes(activeTab)) {
      setActiveTab(tabIds[0] || 'settings-productions');
    }
  }, [tabIds, activeTab]);
  useEffect(() => {
    try {
      localStorage.setItem('sfx_admin_settings_active_tab', activeTab);
      if (window.location.hash.replace(/^#/, '') !== activeTab) {
        // Preserve `window.history.state` — the data router's entry index (R14).
        window.history.replaceState(window.history.state, '', `#${activeTab}`);
      }
    } catch {}
  }, [activeTab]);
  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.replace(/^#/, '');
      if (tabIds.includes(hash)) setActiveTab(hash);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [tabIds]);

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userFormData, setUserFormData] = useState({ new_username: '', department: '', email: '', phone: '', sub_department: '' });
  const [savingUser, setSavingUser] = useState(false);
  const [userError, setUserError] = useState(null);
  const [userSuccess, setUserSuccess] = useState('');
  const usersSectionRef = useRef(null);
  const [userProductionAssignments, setUserProductionAssignments] = useState({}); // { user_id: [production_id, ...] }
  const [savingUserProductions, setSavingUserProductions] = useState(false);

  // Sub-departments state
  const [subDepartments, setSubDepartments] = useState([]);
  const [addingSubDept, setAddingSubDept] = useState(false);
  const [newSubDeptName, setNewSubDeptName] = useState('');
  const [subDeptFilterDeptId, setSubDeptFilterDeptId] = useState('');
  // Sub-Departments management tab state
  const [subDeptError, setSubDeptError] = useState('');
  const [savingSubDept, setSavingSubDept] = useState(false);
  const [addingSubDeptForDeptId, setAddingSubDeptForDeptId] = useState(null);
  const [newSubDeptNameByDept, setNewSubDeptNameByDept] = useState({}); // { [deptId]: name }
  const [editingSubDeptId, setEditingSubDeptId] = useState(null);
  const [editingSubDeptName, setEditingSubDeptName] = useState('');
  const [confirmDeleteSubDeptId, setConfirmDeleteSubDeptId] = useState(null);
  const [subDeptProductionFilter, setSubDeptProductionFilter] = useState('');

  // Departments state
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  // The admin "all departments" list excludes personal "My Space" departments
  // by default (owner ruling 2026-09-24) — mirrors `showPersonalProductions`
  // below, same default OFF, same no-persistence (that sibling toggle does
  // not write to localStorage either, so this one does not invent a key).
  const [showPersonalDepartments, setShowPersonalDepartments] = useState(false);
  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [workflowsSectionCollapsed, setWorkflowsSectionCollapsed] = useState(false);
  const [deptFormData, setDeptFormData] = useState({ name: '', production_id: '' });
  const [savingDepartment, setSavingDepartment] = useState(false);
  const [editingDepartmentId, setEditingDepartmentId] = useState(null);
  const [deptFilterProductionId, setDeptFilterProductionId] = useState('');
  const [expandedDeptId, setExpandedDeptId] = useState(null);
  const [deptMembers, setDeptMembers] = useState({});
  const [addingMemberDeptId, setAddingMemberDeptId] = useState(null);
  // Sub-departments keyed by department_id. Populated when a dept is expanded
  // so both the + Add person panel and the existing-row editable Sub Dept
  // select can render options.
  const [subDeptsByDept, setSubDeptsByDept] = useState({});

  // Workflow templates state for user assignment
  const [workflowTemplates, setWorkflowTemplates] = useState([]);
  const [userWorkflows, setUserWorkflows] = useState({}); // { username: workflowInfo }
  const [applyingWorkflow, setApplyingWorkflow] = useState(null); // username currently being assigned

  // Roles state
  const [roleDefinitions, setRoleDefinitions] = useState([]);
  // DELETED 2026-08-06: `userRoleAssignments` state, `fetchUserRoleAssignments`
  // and `handleAssignRole`. The last of those was a live, unreferenced
  // `PUT /api/roles/user-assignments/:userId` — a handler that writes the
  // unscoped `user_roles` row with no UI anywhere calling it, and the fetch
  // existed only to populate state nothing read. Dead code that assigns roles is
  // exactly what no audit finds; PeopleManager owns this surface. See
  // docs/ROLES.md §F.2 (c).
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [roleFormData, setRoleFormData] = useState({});
  const [savingRole, setSavingRole] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const [roleSuccess, setRoleSuccess] = useState('');
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRolePermissions, setNewRolePermissions] = useState({
    can_create_orders: true, can_view_own_orders: true, can_view_all_orders: false,
    can_approve_orders: false, can_view_admin_dashboard: false, can_manage_settings: false,
    can_view_department_orders: false, can_view_department_dashboard: false, can_manage_roles: false
  });

  const location = useLocation();

  // Resizable columns for each table
  const prodResize = useResizableColumns('sfx_colWidths_productions', {
    company: 180, production: 200, address: 250, actions: 80
  });
  const supplierResize = useResizableColumns('sfx_colWidths_suppliers', {
    name: 180, contact: 140, phone: 120, email: 180, account: 120, actions: 100
  });
  const userResize = useResizableColumns('sfx_colWidths_users', {
    name: 130, role: 100, department: 120, sub_dept: 100, production: 140, email: 160, phone: 100, workflow: 160
  });
  const roleResize = useResizableColumns('sfx_colWidths_roles', {
    role_name: 140, type: 80, actions: 80
  });

  // Column visibility customization per section
  const prodColVis = useColumnVisibility('productions', [
    { key: 'company', label: 'Company' },
    { key: 'production', label: 'Production' },
    { key: 'address', label: 'Address' },
  ]);
  const supplierColVis = useColumnVisibility('suppliers', [
    { key: 'name', label: 'Supplier Name' },
    { key: 'contact', label: 'Contact' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'account', label: 'Account #' },
  ]);
  const userColVis = useColumnVisibility('users', [
    { key: 'name', label: 'Name' },
    { key: 'role', label: 'Role' },
    { key: 'department', label: 'Department' },
    { key: 'sub_dept', label: 'Sub Dept' },
    { key: 'production', label: 'Production' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'workflow', label: 'Approval Workflow' },
  ]);

  // ─────────────────────────────────────────────────────────────────────────
  // THE LABELS THIS CALLER MAY ATTACH A DEPARTMENT TO — the department CREATE
  // form's option list, and NOTHING ELSE ON THIS PAGE.
  //
  // WHY IT IS NARROWER THAN `productions`. `GET /api/productions` returns the
  // labels the caller is assigned under OR created (routes/productions.js:45-55),
  // which answers "which labels may I SEE things under". The create form asks a
  // different question — "which label may I ATTACH a new department to" — and
  // since the ring-fencing ruling (docs/ROLES.md §A.3, 2026-08-08) the answer is
  // the CREATOR alone: `POST /api/departments` admits an existing production_id
  // on `isGlobalAdmin || created_by_user_id === req.userId`
  // (routes/departments.js). Offering the wider set here means offering options
  // that come back as a `production_not_yours` toast on save, which is why the
  // narrowing and this filter are one change and not two.
  //
  // 🚨 THE FILTER DROPDOWNS KEEP THE FULL LIST, DELIBERATELY — the two
  // `Filter by Production` selects below (`dept-filter-production`,
  // `subdept-filter-production`) and the Productions list itself. They ask the
  // "which labels can I SEE things under" question, so narrowing them would hide
  // a caller's own departments from their own screen — a department seated to
  // you by the label's creator lives under a label you did not create.
  // Collapsing the two lists onto one will look like tidying; it is not. Pinned
  // by `AdminSettings.deptFormProductions.test.js`.
  //
  // ⚠️ The task brief that produced this filter also named "the invite
  // dropdown". There is no longer one on this page: the invite flow moved into
  // `components/PeopleManager.js` (see the note at the Users section), which
  // fetches its own data and has no production select at all. The two dead
  // lists that fed from `/api/dashboard/user-productions` were DELETED
  // 2026-09-23 (pre-deletion SHA `fd1271b7`) with the invitations block.
  //
  // 🪤 A NULL `created_by_user_id` MEANS NOBODY, NOT EVERYBODY. Legacy labels
  // predating self-service setup recorded no creator, and the column is
  // `ON DELETE SET NULL`, so a deleted account orphans its labels (6 of 11
  // non-personal productions on the dev database, 2026-08-08). A truthiness test
  // — `!p.created_by_user_id || p.created_by_user_id === currentUserId` — would
  // re-offer exactly the orphaned labels the server now refuses. An unowned
  // label belongs to nobody, so only a site admin may attach to it; the
  // comparison is to the caller's id and never to truthiness.
  const ownsProductionLabel = (p) => (
    currentUserId !== null
    && p.created_by_user_id !== null
    && p.created_by_user_id !== undefined
    && Number(p.created_by_user_id) === currentUserId
  );
  // 🔎 `departmentForm` is ALSO the EDIT form, and this list is deliberately not
  // special-cased for it, because the edit path cannot reach a foreign label
  // today: the Edit/Delete controls on a department row are gated on
  // `isCurrentUserAdmin` (see the comment beside them below), and an admin gets
  // the unfiltered list on the line above. If that button is ever opened to
  // buyers/supervisors — which ruling 5's direction of travel makes likely —
  // a department sitting under a label the caller did not create will render a
  // BLANK Production field, because the stored id has no option to select. The
  // save still works (the state keeps the id, and `PUT /api/departments/:id`
  // compares "is it CHANGING", not "is it present"), so it is a display bug, not
  // a lockout. Fix it then by admitting the row's OWN current label, never by
  // widening this list.
  // 🔻 THE DISPLAY BUG PREDICTED DIRECTLY ABOVE IS NOW LIVE, AND THIS IS THE FIX
  // IT PRESCRIBED (2026-08-10). The department Edit button opened to
  // settings-holders on this date, exactly as "ruling 5's direction of travel"
  // said it would, so a non-admin can now edit a department sitting under a
  // label they did not create — and the stored `production_id` had no option to
  // select, rendering the Production field BLANK. Admitting the row's OWN
  // current label is what was prescribed and it is what this does; the list is
  // NOT widened, because that would re-offer labels the caller may not attach to
  // and `POST /api/departments` would refuse them with `production_not_yours`.
  //
  // Harmless on the CREATE path: `editingDepartmentId` is null there, so nothing
  // is added. `PUT /api/departments/:id` compares "is production_id CHANGING",
  // not "is it present", so re-sending the row's own label is not a tenancy
  // change and stays out of the admin-only branch.
  const editingDepartmentProductionId = useMemo(() => {
    if (editingDepartmentId === null) return null;
    const row = departments.find(d => d.id === editingDepartmentId);
    return row ? row.production_id : null;
  }, [editingDepartmentId, departments]);
  const deptFormProductions = useMemo(
    () => {
      const base = isCurrentUserAdmin ? productions : productions.filter(ownsProductionLabel);
      if (editingDepartmentProductionId === null) return base;
      if (base.some(p => Number(p.id) === Number(editingDepartmentProductionId))) return base;
      const own = productions.find(p => Number(p.id) === Number(editingDepartmentProductionId));
      return own ? [...base, own] : base;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productions, isCurrentUserAdmin, currentUserId, editingDepartmentProductionId]
  );

  useEffect(() => {
    fetchProductions();
    fetchSuppliers();
    fetchUsers();
    fetchWorkflowTemplates();
    fetchUserWorkflows();
    fetchSubDepartments();
    fetchRoleDefinitions();
  }, []);

  // Re-fetch roles when active department/production changes (custom roles are production-scoped)
  useEffect(() => {
    if (activeDepartment?.production_id) {
      fetchRoleDefinitions();
    }
  }, [activeDepartment?.production_id]);

  // Fetch departments (and re-fetch when the production filter or the
  // personal-workspaces toggle changes)
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setDepartmentsLoading(true);
        const params = [];
        if (deptFilterProductionId) params.push(`production_id=${deptFilterProductionId}`);
        if (showPersonalDepartments) params.push('includePersonal=true');
        const url = params.length ? `/api/departments?${params.join('&')}` : '/api/departments';
        const res = await apiGet(url);
        if (!res.ok) throw new Error('Failed to fetch departments');
        const data = await res.json();
        setDepartments(data);
      } catch (err) {
        console.error('[AdminSettings] Failed to fetch departments:', err);
      } finally {
        setDepartmentsLoading(false);
      }
    };
    loadDepartments();
  }, [deptFilterProductionId, showPersonalDepartments]);

  // Re-fetch sub-departments when department filter changes
  useEffect(() => {
    fetchSubDepartments(subDeptFilterDeptId || undefined);
  }, [subDeptFilterDeptId]);

  // Scroll to users section if hash is #users
  useEffect(() => {
    if (location.hash === '#users' && usersSectionRef.current && !usersLoading) {
      usersSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash, usersLoading]);

  // Close the supplier-name autocomplete on an outside click. `mousedown`, not
  // `click`, matching `OrderForm.js` and `ui/Dropdown` — a click that starts
  // outside the panel should dismiss it before the mouse-up lands anywhere.
  useEffect(() => {
    function handleClickOutside(e) {
      if (supplierNameRef.current && !supplierNameRef.current.contains(e.target)) {
        setSupplierNameListOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      const res = await apiGet('/api/suppliers');
      if (!res.ok) throw new Error('Failed to fetch suppliers');
      const data = await res.json();
      setSuppliers(data);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setSuppliersLoading(false);
    }

    // The second tier, fetched separately and deliberately NOT allowed to fail
    // the screen: a supplier list that loads without its suggestions is fully
    // usable, whereas a 500 here must not blank the Suppliers tab.
    try {
      const res = await apiGet('/api/suppliers/name-suggestions');
      setSupplierNameSuggestions(res.ok ? await res.json() : []);
    } catch {
      setSupplierNameSuggestions([]);
    }
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const res = await apiGet('/api/dashboard/admin/users');
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data);
      // Build production assignments map from user data (keyed by user_id)
      const prodMap = {};
      for (const u of data) {
        if (u.assigned_productions && u.assigned_productions.length > 0) {
          prodMap[u.user_id] = u.assigned_productions.map(p => p.production_id);
        }
      }
      setUserProductionAssignments(prev => ({ ...prev, ...prodMap }));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchSubDepartments = async (departmentId) => {
    try {
      const url = departmentId ? `/api/sub-departments?department_id=${departmentId}` : '/api/sub-departments';
      const res = await apiGet(url);
      if (res.ok) {
        const data = await res.json();
        setSubDepartments(data);
      }
    } catch (err) {
      console.error('Failed to fetch sub-departments:', err);
    }
  };

  // Add a sub-department to a specific department (Sub-Departments tab).
  const handleAddSubDepartment = async (departmentId) => {
    const trimmed = (newSubDeptNameByDept[departmentId] || '').trim();
    if (!trimmed) return;
    setSubDeptError('');
    try {
      setAddingSubDept(true);
      const res = await apiPost('/api/sub-departments', {
        name: trimmed,
        department_id: parseInt(departmentId, 10),
        created_by: localStorage.getItem('sfx_requested_by') || 'Admin',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add sub-department');
      setSubDepartments(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewSubDeptNameByDept(prev => ({ ...prev, [departmentId]: '' }));
      setAddingSubDeptForDeptId(null);
    } catch (err) {
      setSubDeptError(err.message);
    } finally {
      setAddingSubDept(false);
    }
  };

  // Rename a sub-department (cascades to orders server-side).
  const handleRenameSubDepartment = async (id) => {
    const trimmed = editingSubDeptName.trim();
    if (!trimmed) return;
    setSubDeptError('');
    try {
      setSavingSubDept(true);
      const res = await apiPut(`/api/sub-departments/${id}`, { name: trimmed });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to rename sub-department');
      setSubDepartments(prev => prev.map(sd => (sd.id === id ? data : sd))
        .sort((a, b) => a.name.localeCompare(b.name)));
      setEditingSubDeptId(null);
      setEditingSubDeptName('');
    } catch (err) {
      setSubDeptError(err.message);
    } finally {
      setSavingSubDept(false);
    }
  };

  // The shooting-unit flag (calculated overtime, 2026-10-26) — the rename route, flag only.
  const handleToggleShootingUnit = async (id, checked) => {
    setSubDeptError('');
    try {
      setSavingSubDept(true);
      const res = await apiPut(`/api/sub-departments/${id}`, { is_shooting_unit: checked });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not change the shooting-unit setting');
      setSubDepartments(prev => prev.map(sd => (sd.id === id ? data : sd)));
    } catch (err) {
      setSubDeptError(err.message);
    } finally {
      setSavingSubDept(false);
    }
  };

  // Delete a sub-department. Cascades server-side: any approval workflow scoped to
  // it is removed and members are unassigned (sub_department_id -> NULL).
  const handleDeleteSubDepartment = async (id) => {
    setSubDeptError('');
    try {
      const res = await apiDelete(`/api/sub-departments/${id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete sub-department');
      setSubDepartments(prev => prev.filter(sd => sd.id !== id));
      setConfirmDeleteSubDeptId(null);
    } catch (err) {
      setSubDeptError(err.message);
    }
  };

  // === Department Management Functions ===
  // `includePersonal` defaults to the current toggle state, same shape as
  // `fetchProductions(includePersonal = showPersonalProductions)` below — a
  // post-mutation refresh (member add/remove) must not silently drop back to
  // hiding personal spaces while the toggle is ON.
  // `quiet`: refresh the list WITHOUT the loading state, which replaces the
  // whole list (and every expanded card) with a spinner — for a refresh after
  // an in-card action such as adding a member.
  const fetchDepartments = async (filterProdId, includePersonal = showPersonalDepartments, { quiet = false } = {}) => {
    try {
      if (!quiet) setDepartmentsLoading(true);
      const prodFilter = filterProdId !== undefined ? filterProdId : deptFilterProductionId;
      const params = [];
      if (prodFilter) params.push(`production_id=${prodFilter}`);
      if (includePersonal) params.push('includePersonal=true');
      const url = params.length ? `/api/departments?${params.join('&')}` : '/api/departments';
      const res = await apiGet(url);
      if (!res.ok) throw new Error('Failed to fetch departments');
      const data = await res.json();
      setDepartments(data);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const handleCreateDepartment = async () => {
    const trimmed = deptFormData.name.trim();
    if (!trimmed || !deptFormData.production_id) {
      setError('Department name and production are required');
      return;
    }
    try {
      setSavingDepartment(true);
      setError(null);
      const res = await apiPost('/api/departments', {
        name: trimmed,
        production_id: parseInt(deptFormData.production_id, 10)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create department');
      setDepartments(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setDeptFormData({ name: '', production_id: '' });
      setShowAddDepartment(false);
      // POST /api/departments SEATS THE CREATOR (routes/departments.js — a plain
      // INSERT into department_assignments inside the same transaction), so the
      // caller's own assignment list has just changed and the department
      // switcher is stale until something refetches it. This is the reported
      // bug: "I've created the second production and the new department within
      // it but it's not on the production dropdown list… I have to refresh the
      // page then it appears."
      //
      // ORDERING: fired on the SUCCESS PATH, after `res.ok`, never
      // optimistically. The route commits its transaction and only then
      // responds (`res.status(201)` sits after `await db.transaction(...)`), so
      // the row `GET /api/roles/my-assignments` reads is already visible by the
      // time we get here. There is no window in which the refetch can beat the
      // write.
      //
      // ⚠️ DELIBERATELY DOES NOTHING FOR A SITE ADMIN, and that is not a bug
      // here. `seat.seatRole` is null for a site admin — they get no
      // `department_assignments` row because they need none — so the refetch
      // correctly returns the same (empty) assignment list and the switcher
      // still shows "All Productions". If someone reports "the list did not
      // update", check whether they are a site admin BEFORE touching this line:
      // the fix for that would be in the seating rule on the server, not here.
      window.dispatchEvent(new Event('sfx-role-change'));
      setSuccessMessage(`Department "${data.name}" created!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setSavingDepartment(false);
    }
  };

  const handleUpdateDepartment = async () => {
    const trimmed = deptFormData.name.trim();
    if (!trimmed) {
      setError('Department name is required');
      return;
    }
    try {
      setSavingDepartment(true);
      setError(null);
      const res = await apiPut(`/api/departments/${editingDepartmentId}`, {
        name: trimmed,
        production_id: deptFormData.production_id ? parseInt(deptFormData.production_id, 10) : undefined
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update department');
      setDepartments(prev => prev.map(d => d.id === editingDepartmentId ? data : d));
      setEditingDepartmentId(null);
      setDeptFormData({ name: '', production_id: '' });
      // The switcher renders `department_name` (ProductionDepartmentSwitcher.js
      // formatAssignment), and a rename here changes it for anyone seated in
      // this department — including the caller. Same staleness, same fix.
      window.dispatchEvent(new Event('sfx-role-change'));
      setSuccessMessage(`Department "${data.name}" updated!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setSavingDepartment(false);
    }
  };

  // Department / production delete — ONE ConfirmDialog (danger) for both,
  // replacing window.confirm (2026-10-08). `force` is no longer sent: the
  // server never deletes a department with orders (owner ruling).
  const [pendingDelete, setPendingDelete] = useState(null); // { kind, item }
  const [deleting, setDeleting] = useState(false);
  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    const { kind, item } = pendingDelete;
    const isDept = kind === 'department';
    const label = isDept ? item.name : item.production_name;
    setDeleting(true);
    try {
      setError(null);
      const res = await apiDelete(isDept ? `/api/departments/${item.id}` : `/api/productions/${item.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Failed to delete ${kind}`);
      if (isDept) {
        setDepartments(prev => prev.filter(d => d.id !== item.id));
        // Deleting a department removes every seat on it, so if the caller was
        // seated in it their own list just shrank and the switcher is showing a
        // department that no longer exists — re-resolve somewhere real.
        window.dispatchEvent(new Event('sfx-role-change'));
      } else {
        setProductions(prev => prev.filter(p => p.id !== item.id));
      }
      setSuccessMessage(`${isDept ? 'Department' : 'Production'} "${label}" deleted`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const handleEditDepartment = (dept) => {
    setEditingDepartmentId(dept.id);
    setDeptFormData({ name: dept.name, production_id: String(dept.production_id) });
    setShowAddDepartment(false);
  };

  const handleCancelDeptEdit = () => {
    setEditingDepartmentId(null);
    setShowAddDepartment(false);
    setDeptFormData({ name: '', production_id: '' });
    setError(null);
  };

  const fetchDeptMembers = async (deptId) => {
    try {
      const res = await apiGet(`/api/departments/${deptId}/members`);
      if (res.ok) {
        const data = await res.json();
        setDeptMembers(prev => ({ ...prev, [deptId]: data }));
      }
    } catch (err) {
      console.error('Failed to fetch department members:', err);
    }
  };

  const handleToggleDeptExpand = (deptId) => {
    if (expandedDeptId === deptId) {
      setExpandedDeptId(null);
    } else {
      setExpandedDeptId(deptId);
      if (!deptMembers[deptId]) {
        fetchDeptMembers(deptId);
      }
      // Fetch sub-departments for this dept so the existing-row Sub Dept select
      // and the + Add person panel's Sub Dept picker both have their options.
      if (!subDeptsByDept[deptId]) {
        apiGet(`/api/sub-departments?department_id=${deptId}`)
          .then(r => r.ok ? r.json() : [])
          .then(data => setSubDeptsByDept(prev => ({ ...prev, [deptId]: Array.isArray(data) ? data : [] })))
          .catch(() => {});
      }
    }
  };

  const handleUpdateMemberRole = async (deptId, userId, newRole) => {
    try {
      const res = await apiPut(`/api/departments/${deptId}/members/${userId}`, { role: newRole });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update role');
      }
      fetchDeptMembers(deptId);
      // `da.role` is rendered as the RoleBadge on every switcher entry and is
      // what `activeRole` / `capabilities` are derived from, so a role change
      // on the caller's own row must re-resolve the context, not just the
      // members table.
      window.dispatchEvent(new Event('sfx-role-change'));
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  // Update a member's sub_department assignment. Ghost rows (user_id IS NULL)
  // can't be targeted by the user-id-keyed PUT route; those must be deleted
  // via the assignment-id route instead.
  const handleUpdateMemberSubDept = async (deptId, userId, newSubDeptIdRaw) => {
    if (!userId) return;
    const newSubDeptId = newSubDeptIdRaw ? parseInt(newSubDeptIdRaw, 10) : null;
    try {
      const res = await apiPut(`/api/departments/${deptId}/members/${userId}`, {
        sub_department_id: newSubDeptId,
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update sub-department');
      }
      fetchDeptMembers(deptId);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  // Long-press timer for mobile member removal
  const longPressTimer = useRef(null);
  const startLongPress = (deptId, member) => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      handleRemoveDeptMember(deptId, member);
      longPressTimer.current = null;
    }, 600);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Remove-member asks through the house ConfirmDialog (review E1, 2026-10-08),
  // same wording as the window.confirm it replaced; a 409 last_department_manager
  // arrives as the server's sentence in the error banner.
  const [removeMemberTarget, setRemoveMemberTarget] = useState(null); // { deptId, member }
  const handleRemoveDeptMember = (deptId, member) => setRemoveMemberTarget({ deptId, member });
  const confirmRemoveDeptMember = async () => {
    const target = removeMemberTarget;
    setRemoveMemberTarget(null);
    if (!target) return;
    const { deptId, member } = target;
    try {
      // Ghost rows (user_id IS NULL) can't be targeted by the user-id route;
      // fall back to the assignment-id route so the admin can always clean up.
      const url = member.user_id
        ? `/api/departments/${deptId}/members/${member.user_id}`
        : `/api/departments/${deptId}/assignments/${member.id}`;
      const res = await apiDelete(url);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove member');
      }
      setDeptMembers(prev => ({
        ...prev,
        // Filter by assignment id so the correct row drops in both paths.
        [deptId]: (prev[deptId] || []).filter(m => m.id !== member.id)
      }));
      // May be the caller removing themselves — their own assignment list has
      // shrunk and the switcher must drop the entry.
      window.dispatchEvent(new Event('sfx-role-change'));
      setSuccessMessage(`${member.username} removed from department`);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchDepartments();
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  const fetchWorkflowTemplates = async () => {
    try {
      const res = await apiGet('/api/approvals/templates');
      if (res.ok) {
        const data = await res.json();
        setWorkflowTemplates(data);
      }
    } catch (err) {
      console.error('Failed to fetch workflow templates:', err);
    }
  };

  const fetchUserWorkflows = async () => {
    try {
      const res = await apiGet('/api/approvals/workflows');
      if (res.ok) {
        const data = await res.json();
        const map = {};
        data.forEach(w => {
          if (w.is_active) {
            map[w.user_name] = w;
          }
        });
        setUserWorkflows(map);
      }
    } catch (err) {
      console.error('Failed to fetch user workflows:', err);
    }
  };

  const handleApplyTemplate = async (username, templateId) => {
    if (!templateId) return;
    try {
      setApplyingWorkflow(username);
      const res = await apiFetch(`/api/approvals/templates/${templateId}/apply/${encodeURIComponent(username)}`, {
        method: 'POST'
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to apply template');
      }
      setUserSuccess(`Workflow template applied to ${username}!`);
      setTimeout(() => setUserSuccess(''), 3000);
      fetchUserWorkflows();
    } catch (err) {
      setUserError(err.message);
      setTimeout(() => setUserError(null), 3000);
    } finally {
      setApplyingWorkflow(null);
    }
  };

  const handleRemoveWorkflow = async (username) => {
    const workflow = userWorkflows[username];
    if (!workflow) return;
    if (!window.confirm(`Remove active approval workflow from ${username}?`)) return;
    try {
      setApplyingWorkflow(username);
      const res = await apiDelete(`/api/approvals/workflows/${workflow.id}`);
      if (!res.ok) throw new Error('Failed to remove workflow');
      setUserSuccess(`Workflow removed from ${username}!`);
      setTimeout(() => setUserSuccess(''), 3000);
      fetchUserWorkflows();
    } catch (err) {
      setUserError(err.message);
      setTimeout(() => setUserError(null), 3000);
    } finally {
      setApplyingWorkflow(null);
    }
  };

  // === Role Management Functions ===
  const fetchRoleDefinitions = async () => {
    try {
      const prodId = activeDepartment?.production_id;
      const url = prodId ? `/api/roles?production_id=${prodId}` : '/api/roles';
      const res = await apiGet(url);
      if (res.ok) {
        const data = await res.json();
        setRoleDefinitions(data);
      }
    } catch (err) {
      console.error('Failed to fetch role definitions:', err);
    }
  };

  const handleSaveRole = async (roleId) => {
    try {
      setSavingRole(true);
      setRoleError(null);
      const res = await apiPut(`/api/roles/${roleId}`, roleFormData);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update role');
      setRoleSuccess(`Updated role "${data.display_name}"`);
      setEditingRoleId(null);
      fetchRoleDefinitions();
      setTimeout(() => setRoleSuccess(''), 3000);
    } catch (err) {
      setRoleError(err.message);
    } finally {
      setSavingRole(false);
    }
  };

  const handleCreateRole = async () => {
    const trimmed = newRoleName.trim();
    if (!trimmed) {
      setRoleError('Role name is required');
      return;
    }
    try {
      setSavingRole(true);
      setRoleError(null);
      const body = { display_name: trimmed, ...newRolePermissions };
      // Every NON-ADMIN author scopes custom roles to their active production —
      // buyers and, since the 2026-08-04 ruling, anyone holding
      // `canAssignDepartmentRoles` in the department they are standing in. The
      // server requires a production_id from a non-admin and re-checks access to
      // it, so this is a convenience, not the gate.
      if (!isCurrentUserAdmin && activeDepartment?.production_id) {
        body.production_id = activeDepartment.production_id;
      }
      const res = await apiPost('/api/roles', body);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create role');
      setRoleSuccess(`Created role "${data.display_name}"`);
      setShowAddRole(false);
      setNewRoleName('');
      setNewRolePermissions({
        can_create_orders: true, can_view_own_orders: true, can_view_all_orders: false,
        can_approve_orders: false, can_view_admin_dashboard: false, can_manage_settings: false,
        can_view_department_orders: false, can_view_department_dashboard: false, can_manage_roles: false
      });
      fetchRoleDefinitions();
      setTimeout(() => setRoleSuccess(''), 3000);
    } catch (err) {
      setRoleError(err.message);
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      setRoleError(null);
      const res = await apiDelete(`/api/roles/${roleId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete role');
      setRoleSuccess('Role deleted');
      fetchRoleDefinitions();
      setTimeout(() => setRoleSuccess(''), 3000);
    } catch (err) {
      setRoleError(err.message);
    }
  };

  const PERMISSION_LABELS = {
    can_create_orders: 'Create Orders',
    can_view_own_orders: 'View Own Orders',
    can_view_all_orders: 'View All Orders',
    can_approve_orders: 'Approve/Reject Orders',
    can_view_admin_dashboard: 'View Admin Dashboard',
    can_manage_settings: 'Manage Admin Settings',
    can_view_department_orders: 'View Department Orders',
    can_view_department_dashboard: 'View Department Dashboard',
    can_manage_roles: 'Manage Roles',
    can_view_budget: 'View Budget',
    can_manage_budget: 'Manage Budget',
  };

  // Owner ruling 2026-08-15 ("going horizontal will save vertical spce as
  // well"): the roles-table column headers are HORIZONTAL with these
  // shortened labels ("Dash" for Dashboard, "Dep" for Department — his
  // examples). The full PERMISSION_LABELS string stays the load-bearing name
  // everywhere else (edit-form checkboxes, create-role form, tooltips,
  // sr-only copies); this map is DISPLAY ONLY for the header row. Every
  // value must stay unique — two identical headings would be
  // indistinguishable on a 14-column table. Header row measured 192.61px
  // (vertical labels) -> 36.5px with these, in Chromium against the
  // compiled stylesheet.
  const PERMISSION_LABELS_SHORT = {
    can_create_orders: 'Create',
    can_view_own_orders: 'Own Orders',
    can_view_all_orders: 'All Orders',
    can_approve_orders: 'Approve',
    can_view_admin_dashboard: 'Admin Dash',
    can_manage_settings: 'Admin Settings',
    can_view_department_orders: 'Dep Orders',
    can_view_department_dashboard: 'Dep Dash',
    can_manage_roles: 'Roles',
    can_view_budget: 'Budget',
    can_manage_budget: 'Manage Budget',
  };

  const handleEditUser = (user) => {
    setEditingUserId(user.user_id);
    setUserFormData({
      new_username: user.username,
      department: user.department || '',
      email: user.email || '',
      phone: user.phone || '',
      sub_department: user.sub_department || '',
    });
    setNewSubDeptName('');
    setUserError(null);
    setUserSuccess('');
  };

  const handleCancelEditUser = () => {
    setEditingUserId(null);
    setUserFormData({ new_username: '', department: '', email: '', phone: '', sub_department: '' });
    setNewSubDeptName('');
    setUserError(null);
  };

  const handleSaveUser = async () => {
    if (!userFormData.new_username.trim()) {
      setUserError('Name is required');
      return;
    }
    try {
      setSavingUser(true);
      setUserError(null);
      const res = await apiPut(`/api/dashboard/admin/users/${editingUserId}`, userFormData);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update user');
      setUserSuccess(`Updated ${data.username} (${data.updated_orders} order${data.updated_orders !== 1 ? 's' : ''} updated)`);
      // Also save production assignments if changed
      const currentProds = userProductionAssignments[editingUserId] || [];
      try {
        await apiPut(`/api/dashboard/admin/users/${editingUserId}/productions`, { production_ids: currentProds });
      } catch (e) {
        // production assignment save failed but user save succeeded
        console.error('Failed to save production assignments:', e);
      }
      setEditingUserId(null);
      fetchUsers();
      setTimeout(() => setUserSuccess(''), 4000);
    } catch (err) {
      setUserError(err.message);
    } finally {
      setSavingUser(false);
    }
  };

  const handleToggleUserProduction = (userId, productionId) => {
    setUserProductionAssignments(prev => {
      const current = prev[userId] || [];
      if (current.includes(productionId)) {
        const updated = current.filter(id => id !== productionId);
        return { ...prev, [userId]: updated };
      } else {
        return { ...prev, [userId]: [...current, productionId] };
      }
    });
  };

  const handleAddSupplier = async (e) => {
    e.preventDefault();
    const trimmed = supplierFormData.name.trim();
    if (!trimmed) {
      setError('Supplier name is required');
      return;
    }
    try {
      setSavingSupplier(true);
      setError(null);
      const payload = {
        name: trimmed,
        contact_name: supplierFormData.contact_name.trim() || null,
        phone: supplierFormData.phone.trim() || null,
        email: supplierFormData.email.trim() || null,
        website: supplierFormData.website.trim() || null,
        account_number: supplierFormData.account_number.trim() || null
      };
      const res = await apiPost('/api/suppliers', payload);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to add supplier');
      }
      const newSupplier = await res.json();
      setSuppliers(prev => [...prev, newSupplier].sort((a, b) => a.name.localeCompare(b.name)));
      setSupplierFormData({ name: '', contact_name: '', phone: '', email: '', website: '', account_number: '' });
      setShowAddSupplier(false);
      setSuccessMessage(`Supplier "${newSupplier.name}" added successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setSavingSupplier(false);
    }
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplierId(supplier.id);
    setSupplierFormData({
      name: supplier.name,
      contact_name: supplier.contact_name || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      website: supplier.website || '',
      account_number: supplier.account_number || ''
    });
    setShowAddSupplier(false);
    setError(null);
  };

  const handleUpdateSupplier = async (e) => {
    e.preventDefault();
    const trimmed = supplierFormData.name.trim();
    if (!trimmed) {
      setError('Supplier name is required');
      return;
    }
    try {
      setSavingSupplier(true);
      setError(null);
      const payload = {
        name: trimmed,
        contact_name: supplierFormData.contact_name.trim() || null,
        phone: supplierFormData.phone.trim() || null,
        email: supplierFormData.email.trim() || null,
        website: supplierFormData.website.trim() || null,
        account_number: supplierFormData.account_number.trim() || null
      };
      const res = await apiPut(`/api/suppliers/${editingSupplierId}`, payload);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to update supplier');
      }
      const updated = await res.json();
      setSuppliers(prev => prev.map(s => s.id === editingSupplierId ? updated : s).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingSupplierId(null);
      setSupplierFormData({ name: '', contact_name: '', phone: '', email: '', website: '', account_number: '' });
      setSuccessMessage(`Supplier "${updated.name}" updated successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setSavingSupplier(false);
    }
  };

  const handleCancelSupplierEdit = () => {
    setEditingSupplierId(null);
    setShowAddSupplier(false);
    setSupplierFormData({ name: '', contact_name: '', phone: '', email: '', website: '', account_number: '' });
    setError(null);
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!window.confirm(`Are you sure you want to delete "${supplier.name}"?`)) return;
    try {
      setDeletingSupplier(supplier.id);
      setError(null);
      const res = await apiDelete(`/api/suppliers/${supplier.id}`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to delete supplier');
      }
      setSuppliers(prev => prev.filter(s => s.id !== supplier.id));
      setSuccessMessage(`Supplier "${supplier.name}" deleted successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setDeletingSupplier(null);
    }
  };

  const fetchProductions = async (includePersonal = showPersonalProductions) => {
    try {
      setLoading(true);
      const url = includePersonal
        ? '/api/productions?includePersonal=true'
        : '/api/productions';
      const res = await apiGet(url);
      if (!res.ok) throw new Error('Failed to fetch productions');
      const data = await res.json();
      setProductions(data);
      setError(null);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = (prod) => {
    setEditingId(prod.id);
    setFormData({
      company_name: prod.company_name,
      production_name: prod.production_name,
      address: prod.address || '',
      timesheet_agreement: agreementFamilyValue(prod.timesheet_agreement),
      // The agreement figures' DRAFT (2026-10-28): null until a figure is touched.
      agreement_draft: null,
    });
    setShowAddForm(false);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_name.trim() || !formData.production_name.trim()) {
      setError('Company name and production name are required');
      return;
    }

    const isEditing = editingId !== null;

    try {
      setSaving(true);
      setError(null);
      const url = isEditing ? `/api/productions/${editingId}` : '/api/productions';
      const method = isEditing ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify({
          company_name: formData.company_name.trim(),
          production_name: formData.production_name.trim(),
          address: formData.address.trim() || null,
          // Calculated overtime (2026-10-26): edit only; omitted keeps the stored agreement.
          ...(isEditing && formData.timesheet_agreement ? { timesheet_agreement: formData.timesheet_agreement } : {}),
          // The production's own agreement figures (2026-10-28, Stage 1) — the SAME save, never a second.
          ...(isEditing && formData.agreement_draft ? { timesheet_agreement_overrides: agreementOverridesPayload(formData.agreement_draft) } : {}),
          /* 🔑 `include_vat` IS DELIBERATELY OMITTED, AND OMITTING IT IS THE
             POINT — DO NOT "TIDY" IT BACK IN AS `include_vat: 0`.
             `PUT /api/productions/:id` preserves the stored value when the body
             leaves the field out (`routes/productions.js`, and pinned by
             `tests/api/productions-include-vat.test.js` — "preserves include_vat
             when body omits it"). Sending an explicit 0 would WIPE the stored
             flag on every production an admin so much as renames, destroying the
             data this removal was careful to keep. On CREATE the server defaults
             it to 0, which is the correct new-production state. */
        })
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || (isEditing ? 'Failed to update production' : 'Failed to create production'));
      }
      const savedProduction = await res.json();
      if (isEditing) {
        setProductions(prev => prev.map(p => p.id === editingId ? savedProduction : p).sort((a, b) =>
          (a.company_name + a.production_name).localeCompare(b.company_name + b.production_name)
        ));
        setEditingId(null);
        // A RENAME changes `production_name` / `company_name`, and the switcher
        // renders both (ProductionDepartmentSwitcher.js formatAssignment, via
        // the JOIN in GET /api/roles/my-assignments). Every department under
        // this label — including the caller's — is now showing the old name.
        window.dispatchEvent(new Event('sfx-role-change'));
        setSuccessMessage(`Production "${savedProduction.production_name}" updated successfully!`);
      } else {
        setProductions(prev => [...prev, savedProduction].sort((a, b) =>
          (a.company_name + a.production_name).localeCompare(b.company_name + b.production_name)
        ));
        setShowAddForm(false);
        // ⚠️ NO `sfx-role-change` ON THE CREATE BRANCH, AND THAT IS DELIBERATE —
        // do not "complete the set" by adding one.
        //
        // A production is a bookkeeping LABEL that seats nobody: "creating one
        // grants the creator nothing in any department" (product owner,
        // 2026-08-02; routes/productions.js). The switcher is fed by
        // GET /api/roles/my-assignments, whose list is
        // `FROM department_assignments da … WHERE da.user_id = ?` — so a brand
        // new label with no departments under it CANNOT appear in it. A
        // dispatch here would fire a refetch guaranteed to return a byte-
        // identical list: the same objection that rules out polling, just once
        // instead of forever.
        //
        // The label becomes visible when a DEPARTMENT is created under it, and
        // `handleCreateDepartment` above dispatches for exactly that reason.
        setSuccessMessage(`Production "${savedProduction.production_name}" added successfully!`);
      }
      setFormData({ company_name: '', production_name: '', address: '' });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    setFormData({ company_name: '', production_name: '', address: '' });
    setError(null);
  };

  // ── The supplier NAME autocomplete (2026-08-08) ──────────────────────
  //
  // The SAME two-tier list the order form shows — real `suppliers` records plus
  // names typed on past approved orders — through the SAME merge and the SAME
  // list component, so the two surfaces cannot drift about what a supplier
  // suggestion looks like or which tier is marked.
  //
  // 🔑 IT SUGGESTS; IT DOES NOT CREATE. Picking a row fills the name box and
  // nothing else. Every other field stays empty for the user to complete, which
  // is the point: a suggestion has no account number or contact to prefill, and
  // pre-filling them from a same-named record would silently duplicate a
  // supplier that already exists.
  //
  // Derived at render time from state, never stored — the same reasoning as
  // `OrderForm.js`: both arrays arrive after first paint, and a copy filtered
  // inside a keystroke handler freezes at whatever had loaded at that instant.
  const supplierNameOptions = buildSupplierOptions(suppliers, supplierNameSuggestions);
  const supplierNameMatches = matchSupplierOptions(supplierNameOptions, supplierFormData.name);
  // ⚠️ EXCLUDES THE ROW BEING EDITED. Offering a supplier its own current name
  // back is noise, and selecting it would be a no-op that looks like an action.
  const supplierNameSuggestionsShown = supplierNameMatches.filter(
    (o) => !(editingSupplierId !== null && o.isRecord && o.supplier?.id === editingSupplierId)
  );
  const showSupplierNameList = supplierNameListOpen && supplierNameSuggestionsShown.length > 0;

  // Shared Supplier add/edit form — rendered at the top when adding, or inline below the row when editing
  const supplierForm = (
    <form onSubmit={editingSupplierId !== null ? handleUpdateSupplier : handleAddSupplier} className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
      <h4 className="text-md font-semibold text-gray-700 dark:text-gray-200 mb-3">{editingSupplierId !== null ? 'Edit Supplier' : 'New Supplier'}</h4>
      <div className="space-y-3">
        <div>
          <label htmlFor="supplier_name" className={FIELD_LABEL_CLASS}>
            Supplier Name <span className="text-red-500">*</span>
          </label>
          <div ref={supplierNameRef} className="relative">
            <input
              type="text"
              id="supplier_name"
              value={supplierFormData.name}
              onChange={(e) => {
                setSupplierFormData(prev => ({ ...prev, name: e.target.value }));
                // Typing opens the list; whether anything is IN it is decided by
                // the derived `showSupplierNameList`, so an array that arrives
                // late still lands without a further keystroke.
                setSupplierNameListOpen(true);
              }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              placeholder="e.g. RS Components"
              autoFocus
              autoComplete="off"
              data-testid="admin-supplier-name"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={showSupplierNameList}
              aria-controls="admin-supplier-name-list"
            />
            {showSupplierNameList && (
              <SupplierOptionList
                id="admin-supplier-name-list"
                options={supplierNameSuggestionsShown}
                onSelect={(name) => {
                  setSupplierFormData(prev => ({ ...prev, name }));
                  setSupplierNameListOpen(false);
                }}
                testId="admin-supplier-name-list"
              />
            )}
          </div>
        </div>
        <div>
          <label htmlFor="supplier_contact_name" className={FIELD_LABEL_CLASS}>Contact Name</label>
          <input
            type="text"
            id="supplier_contact_name"
            value={supplierFormData.contact_name}
            onChange={(e) => setSupplierFormData(prev => ({ ...prev, contact_name: e.target.value }))}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            placeholder="e.g. John Smith"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="supplier_phone" className={FIELD_LABEL_CLASS}>Phone</label>
            <input
              type="tel"
              id="supplier_phone"
              value={supplierFormData.phone}
              onChange={(e) => setSupplierFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              placeholder="e.g. 020 7946 0958"
            />
          </div>
          <div>
            <label htmlFor="supplier_email" className={FIELD_LABEL_CLASS}>Email</label>
            <input
              type="email"
              id="supplier_email"
              value={supplierFormData.email}
              onChange={(e) => setSupplierFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              placeholder="e.g. orders@rscomponents.com"
            />
          </div>
          <div>
            <label htmlFor="supplier_website" className={FIELD_LABEL_CLASS}>Website</label>
            <input
              type="url"
              id="supplier_website"
              value={supplierFormData.website}
              onChange={(e) => setSupplierFormData(prev => ({ ...prev, website: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              placeholder="e.g. https://www.rs-online.com"
            />
          </div>
          <div>
            <label htmlFor="supplier_account" className={FIELD_LABEL_CLASS}>Account Number</label>
            <input
              type="text"
              id="supplier_account"
              value={supplierFormData.account_number}
              onChange={(e) => setSupplierFormData(prev => ({ ...prev, account_number: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              placeholder="e.g. ACC-12345"
            />
          </div>
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <button
          type="submit"
          disabled={savingSupplier}
          className={BTN.confirm}
        >
          {savingSupplier ? 'Saving...' : (editingSupplierId !== null ? 'Update Supplier' : 'Save Supplier')}
        </button>
        <button
          type="button"
          onClick={handleCancelSupplierEdit}
          className={BTN.secondary}
        >
          Cancel
        </button>
      </div>
    </form>
  );

  // Shared Department add/edit form — rendered at the top when adding, or inline below the row when editing
  const departmentForm = (
    <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
        {editingDepartmentId ? 'Edit Department' : 'New Department'}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={FIELD_LABEL_CLASS}>Production</label>
          <select
            value={deptFormData.production_id}
            onChange={(e) => setDeptFormData(prev => ({ ...prev, production_id: e.target.value }))}
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-2 md:py-1 text-sm focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
            data-testid="dept-form-production"
          >
            <option value="">Select Production</option>
            {/* `deptFormProductions`, NEVER `productions` — see the comment beside
                its definition. This is the ONE select on the page that asks "which
                label may I ATTACH a department to"; the two `Filter by Production`
                selects ask "which labels can I see things under" and keep the full
                list. They are different questions and must not be collapsed. */}
            {deptFormProductions.map(p => (
              <option key={p.id} value={p.id}>{p.company_name} - {p.production_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={FIELD_LABEL_CLASS}>Department Name</label>
          <input
            type="text"
            value={deptFormData.name}
            onChange={(e) => setDeptFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. SFX, Props, Art"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-2 md:py-1 text-sm focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
            data-testid="dept-form-name"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); editingDepartmentId ? handleUpdateDepartment() : handleCreateDepartment(); }}}
          />
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={editingDepartmentId ? handleUpdateDepartment : handleCreateDepartment}
          disabled={savingDepartment}
          className={BTN.confirm}
          data-testid="save-department-btn"
        >
          {savingDepartment ? 'Saving...' : (editingDepartmentId ? 'Update' : 'Create')}
        </button>
        <button
          onClick={handleCancelDeptEdit}
          className={BTN.secondary}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // Shared Production add/edit form — rendered at the top when adding, or inline below the row when editing
  const isPhone = useIsMobileOrderViewport();
  // The production row's action cell — ONE definition, rendered by the desktop
  // table and the phone card list (mobile batch, item 21).
  const renderProdAction = (prod) => (
    prod.is_personal ? (
      <span className="text-xs text-gray-400 dark:text-gray-500 italic" title="Auto-created personal workspace — not editable">Personal</span>
    ) : prod.can_edit === true ? (
      <span className="inline-flex gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); handleEdit(prod); }}
          className={`${BTN_XS.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
        >
          Edit
        </button>
        {/* Delete: the SERVER's `can_delete` (site admin + no departments,
            owner 2026-10-08) — never re-derived here. */}
        {prod.can_delete === true && (
          <button
            onClick={(e) => { e.stopPropagation(); setPendingDelete({ kind: 'production', item: prod }); }}
            className={`${BTN_XS['danger-soft']} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
            data-testid={`delete-production-${prod.id}`}
          >
            Delete
          </button>
        )}
      </span>
    ) : (
      <span className="text-xs text-gray-400 dark:text-gray-500 italic" title="You can see this label because you work under it, but only its creator or someone with settings rights in one of its departments can rename it.">View only</span>
    )
  );

  const productionForm = (
    <form onSubmit={handleSubmit} className={editingId !== null ? 'p-4' : 'p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg'}>
      <h4 className="text-md font-semibold text-gray-700 dark:text-gray-200 mb-3">{editingId !== null ? 'Edit Production' : 'New Production'}</h4>
      <div className="space-y-3">
        <div>
          <label htmlFor="company_name" className={FIELD_LABEL_CLASS}>
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="company_name"
            name="company_name"
            value={formData.company_name}
            onChange={handleInputChange}
            className={`${FORM_INPUT_FIELD} focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="e.g. Warner Bros"
            required
          />
        </div>
        <div>
          <label htmlFor="production_name" className={FIELD_LABEL_CLASS}>
            Production Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="production_name"
            name="production_name"
            value={formData.production_name}
            onChange={handleInputChange}
            className={`${FORM_INPUT_FIELD} focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder="e.g. The Batman 2"
            required
          />
        </div>
        <div>
          <label htmlFor="address" className={FIELD_LABEL_CLASS}>
            Address
          </label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            rows={2}
            className={FORM_TEXTAREA_FIELD}
            placeholder="e.g. Leavesden Studios, Watford, WD25 7LT"
          />
        </div>
        {/* Timesheets are gated to DEMO departments until launch (2026-10-28) — so is this setting. */}
        {editingId !== null && activeDepartment?.timesheets_enabled === true && (
          <div>
            <label htmlFor="timesheet_agreement" className={FIELD_LABEL_CLASS}>Film or TV (timesheets)</label>
            <select
              id="timesheet_agreement"
              name="timesheet_agreement"
              value={formData.timesheet_agreement || 'film_swd'}
              onChange={handleInputChange}
              data-testid="production-timesheet-agreement"
              // md+: content-width, not full width (owner 2026-10-28: a full-width select
              // "is easy to miss that is a drop down selector"); phones keep full width.
              className={`${FORM_INPUT_FIELD} md:w-auto md:min-w-[18rem] focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              {TIMESHEET_AGREEMENT_OPTIONS.map(({ value: v, label: text }) => <option key={v} value={v}>{text}</option>)}
            </select>
            <AgreementSummary
              productionId={editingId}
              family={String(formData.timesheet_agreement || '').startsWith('tv') ? 'tv' : 'film'}
              value={formData.agreement_draft || null}
              onChange={(draft) => setFormData((prev) => ({ ...prev, agreement_draft: draft }))}
            />
          </div>
        )}
        {/* 🔻 THE "Include VAT (20%) on displayed prices" CHECKBOX WAS REMOVED
            2026-08-08. It was the ONLY way to set `productions.include_vat`, so
            with it gone nothing in the UI writes the column — though the API
            still accepts it (see the submit handler). Product owner: *"actualy
            remove VAT the reason is deeper. differnt items demaded differnt vat
            rates etc so by not having vat the deparment can just decided if they
            type in ex vat proices or inc vat prices."*
            🔑 The checkbox's own helper text is why this cannot simply come
            back: it promised *"All stored prices are entered Ex VAT … the app
            multiplies every displayed figure by 1.20"*. A single production-wide
            ×1.20 is not what UK VAT does — standard, reduced, zero-rated and
            exempt items sit side by side on one order — so the control produced
            confidently wrong totals for any order that was not entirely at the
            standard rate. Full reasoning in `hooks/useVatMode.js`. */}
      </div>
      <div className="flex gap-3 mt-4">
        <button
          type="submit"
          disabled={saving}
          className={BTN.confirm}
        >
          {saving ? 'Saving...' : (editingId !== null ? 'Update Production' : 'Save Production')}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className={BTN.secondary}
        >
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div className="max-w-full p-2 md:p-4 lg:p-6 mobile-fluid-tables" data-mobile-inputs="">
      {/* Mobile-only: override the resizable-column inline widths so admin tables become fluid below md breakpoint. Desktop preserved exactly. */}
      <style>{`@media (max-width: 767px) {
        .mobile-fluid-tables table { table-layout: auto !important; width: 100% !important; min-width: 0 !important; }
        .mobile-fluid-tables col { width: auto !important; }
        .mobile-fluid-tables th, .mobile-fluid-tables td { width: auto !important; }
      }`}</style>

      <ConfirmDialog
        open={!!pendingDelete}
        variant="danger"
        initialFocus="cancel"
        busy={deleting}
        title={pendingDelete?.kind === 'production'
          ? `Delete production "${pendingDelete?.item?.production_name}"?`
          : `Delete department "${pendingDelete?.item?.name}"?`}
        message={pendingDelete?.kind === 'production'
          ? 'The production label and its pending invitations are removed. This cannot be undone.'
          : 'Everything in it is removed with it: members, sub-departments, budgets, crew, cost codes, suppliers, the breakdown, teams and tasks, risk assessments, files and pending invitations. This cannot be undone.'}
        confirmLabel={pendingDelete?.kind === 'production' ? 'Delete production' : 'Delete department'}
        cancelLabel="Keep it"
        onConfirm={handleConfirmDelete}
        onCancel={() => { if (!deleting) setPendingDelete(null); }}
      />
      <ConfirmDialog
        open={!!removeMemberTarget}
        variant="danger"
        initialFocus="cancel"
        title={removeMemberTarget ? `Remove ${removeMemberTarget.member.username} from this department?` : ''}
        message="Their approval steps and team places here are handed over or removed."
        confirmLabel="Remove"
        cancelLabel="Keep them"
        onConfirm={confirmRemoveDeptMember}
        onCancel={() => setRemoveMemberTarget(null)}
      />

      {/* Success Message */}
      {successMessage && (
        <div className={`mb-4 border px-4 py-3 rounded-lg ${successTone === 'warning'
          ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-200'
          : 'bg-green-50 border-green-200 text-green-700'}`} data-testid="settings-success-banner" data-tone={successTone}>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Section tabs — desktop only; mobile keeps the stacked accordion layout */}
      <div className="hidden md:flex flex-wrap items-center gap-1 p-1 bg-gray-100 dark:bg-gray-700/50 rounded-lg w-fit mb-4" role="tablist" aria-label="Admin settings sections">
        {SECTION_TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 min-h-[36px] text-xs font-semibold rounded-md transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-pressed={activeTab === tab.id}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Productions Section — deptSettings since 2026-08-04. A production is a
          bookkeeping label, not an authority tier (CLAUDE.md, "Authority
          model"): any buyer may create one and it confers nothing. The
          personal-productions toggle stays admin-only below. */}
      {visibleTabIds.has('settings-productions') && (
      <CollapsibleSection phoneAccordion
        id="settings-productions"
        title="Productions"
        className={activeTab === 'settings-productions' ? '' : 'md:hidden'}
        forceExpanded={activeTab === 'settings-productions'}
        headerActions={<ColumnCustomizer columns={prodColVis.columns} isVisible={prodColVis.isVisible} toggleColumn={prodColVis.toggleColumn} />}
        onToggle={(nextCollapsed) => { if (nextCollapsed) { setShowAddForm(false); setEditingId(null); setError(null); } }}
      >
        <div className="p-3 md:p-6">
          <div className="flex items-center justify-between mb-4">
            {isCurrentUserAdmin ? (
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showPersonalProductions}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setShowPersonalProductions(next);
                    fetchProductions(next);
                  }}
                  className="rounded"
                />
                Show personal workspaces
              </label>
            ) : <span />}
            {/* CREATING a label is a different question from RENAMING one, and
                the server asks it differently: `POST /api/productions` gates on
                `canSetUp` — do you hold buyer or supervisor in a REAL
                department (helpers/setupAuthority.js) — not on `can_edit`. The
                section is now reachable by a creator whose only seat is `user`,
                and offering them a button that 403s would be the opposite
                defect to the one this change fixes. This is the population that
                had the button before 2026-08-10, plus the settings-holder who
                is merely standing in the wrong department. */}
            {!showAddForm && editingId === null
              && (isCurrentUserAdmin || canManageDeptSettings || holdsDepartmentSettingsSomewhere) && (
              <button
                onClick={() => { setShowAddForm(true); setEditingId(null); }}
                className={BTN.primary}
              >
                + Add Production
              </button>
            )}
          </div>

          {/* Add Production Form (Edit form is rendered inline below the row being edited) */}
          {showAddForm && <div className="mb-6">{productionForm}</div>}

        {/* Productions List */}
        {loading ? (
          <p className="text-gray-500 text-sm">Loading productions...</p>
        ) : productions.length === 0 ? (
          <p className="text-gray-500 text-sm">No productions yet. Click "Add Production" to create one.</p>
        ) : (
          isPhone ? (
          /* 📱 PHONE (< md, mobile batch item 21): stacked cards. The fixed-
              width table truncated both names to ~80px at 375. Same data, same
              server-computed action, same inline edit form under the open row.
              A JS branch, not `md:hidden`: the open row's form must exist ONCE. */
          <ul className="divide-y divide-gray-100 dark:divide-gray-700" data-testid="productions-mobile-list">
            {productions.map((prod) => (
              <li key={prod.id} data-testid={`production-card-${prod.id}`}>
                <div
                  className={`flex items-center gap-2 py-2 min-h-[44px] ${prod.can_edit ? 'cursor-pointer' : ''} ${editingId === prod.id ? 'bg-gray-100 dark:bg-slate-700' : ''}`}
                  onClick={() => { if (!prod.can_edit) return; editingId === prod.id ? handleCancel() : handleEdit(prod); }}
                >
                  {prod.can_edit && <span aria-hidden="true" className={`text-xs text-gray-400 transition-transform duration-200 flex-shrink-0 ${editingId === prod.id ? 'rotate-90' : ''}`}>&#9654;</span>}
                  <div className="flex-1 min-w-0 break-words">
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{prod.production_name}</div>
                    {prod.company_name && <div className="text-xs text-gray-500 dark:text-gray-400">{prod.company_name}</div>}
                  </div>
                  <div className="shrink-0">{renderProdAction(prod)}</div>
                </div>
                {editingId === prod.id && (
                  <div className={EXPANDED_DETAIL_PANEL}>
                    {productionForm}
                  </div>
                )}
              </li>
            ))}
          </ul>
          ) : (
          <div className="overflow-x-auto">
            <table className="text-sm" style={{ tableLayout: 'fixed', minWidth: '100%', width: 'max-content' }}>
              <colgroup>
                {prodColVis.isVisible('company') && <col style={{ width: prodResize.getColWidth('company') }} />}
                {prodColVis.isVisible('production') && <col style={{ width: prodResize.getColWidth('production') }} />}
                {prodColVis.isVisible('address') && <col className="hidden sm:table-column" style={{ width: prodResize.getColWidth('address') }} />}
                <col style={{ width: prodResize.getColWidth('actions') }} />
              </colgroup>
              <thead>
                <tr className="border-b border-gray-200">
                  {prodColVis.isVisible('company') && <ResizableHeader colKey="company" width={prodResize.getColWidth('company')} onResizeStart={prodResize.handleResizeStart} className="text-left py-2 px-1 md:px-3 font-semibold text-gray-600">Company</ResizableHeader>}
                  {prodColVis.isVisible('production') && <ResizableHeader colKey="production" width={prodResize.getColWidth('production')} onResizeStart={prodResize.handleResizeStart} className="text-left py-2 px-1 md:px-3 font-semibold text-gray-600">Production</ResizableHeader>}
                  {prodColVis.isVisible('address') && <ResizableHeader colKey="address" width={prodResize.getColWidth('address')} onResizeStart={prodResize.handleResizeStart} className="text-left py-2 px-3 font-semibold text-gray-600 hidden sm:table-cell">Address</ResizableHeader>}
                  <th className="text-right py-2 px-1 md:px-3 font-semibold text-gray-600" style={{ width: prodResize.getColWidth('actions') }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {productions.map((prod) => (
                  <React.Fragment key={prod.id}>
                    <tr
                      className={`border-b border-gray-100 dark:border-gray-700 ${prod.can_edit ? 'cursor-pointer' : ''} ${editingId === prod.id ? EXPANDED_ROW_FRAME : (prod.can_edit ? 'hover:bg-gray-50 dark:hover:bg-gray-700/30' : '')}`}
                      onClick={() => { if (!prod.can_edit) return; editingId === prod.id ? handleCancel() : handleEdit(prod); }}
                    >
                      {prodColVis.isVisible('company') && <td className="py-2 px-1 md:px-3 text-gray-800 overflow-hidden text-ellipsis">
                        <span className="inline-flex items-center gap-1.5 max-w-full">
                          {prod.can_edit && <span data-chevron className={`text-xs text-gray-400 transition-transform duration-200 flex-shrink-0 ${editingId === prod.id ? 'rotate-90' : ''}`}>&#9654;</span>}
                          <span className="block max-w-[70px] md:max-w-none truncate" title={prod.company_name}>{prod.company_name}</span>
                        </span>
                      </td>}
                      {prodColVis.isVisible('production') && <td className="py-2 px-1 md:px-3 text-gray-800 overflow-hidden text-ellipsis"><span className="block max-w-[85px] md:max-w-none truncate" title={prod.production_name}>{prod.production_name}</span></td>}
                      {prodColVis.isVisible('address') && <td className="py-2 px-3 text-gray-500 hidden sm:table-cell overflow-hidden text-ellipsis">{prod.address || '—'}</td>}
                      {/* 🔑 THE EDIT CONTROL RENDERS FROM THE SERVER'S OWN
                          ANSWER, `can_edit`, AND MUST NEVER BE RE-DERIVED HERE
                          (2026-08-10). The predicate is site admin OR the
                          label's creator OR settings rights in ANY ONE
                          department under the label
                          (server/helpers/productionEditAuthority.js) — and the
                          client CANNOT evaluate the third disjunct, because it
                          never fetches a label's departments. It used to render
                          on `!prod.is_personal` and was reachable only through
                          a section gated on the ACTIVE department's
                          capabilities, which refused a supervisor the server
                          admitted twice over. `=== true` is not decoration:
                          `undefined` from an older cached payload must be a
                          refusal, never a grant. */}
                      <td className="py-2 px-1 md:px-3 text-right">
                        {renderProdAction(prod)}
                      </td>
                    </tr>
                    {editingId === prod.id && (
                      <tr>
                        <td colSpan="4" className="p-0">
                          <div className={EXPANDED_DETAIL_PANEL}>
                            {productionForm}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          )
        )}
        </div>
      </CollapsibleSection>
      )}

      {/* Departments Section — deptSettings (BEHAVIOUR CHANGE: was admin-or-buyer,
          so a supervisor holding the capability here now gains it). Buyers stay
          scoped to their own departments server-side; admin-only controls below
          remain separately gated on isCurrentUserAdmin. */}
      {visibleTabIds.has('settings-departments') && (
      <CollapsibleSection phoneAccordion
        id="settings-departments"
        title="Departments"
        className={`mt-6 ${activeTab === 'settings-departments' ? '' : 'md:hidden'}`}
        forceExpanded={activeTab === 'settings-departments'}
        onToggle={(nextCollapsed) => { if (nextCollapsed) { setShowAddDepartment(false); setEditingDepartmentId(null); setExpandedDeptId(null); setAddingMemberDeptId(null); } }}
      >
        <div className="p-3 md:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2 w-full md:w-auto min-w-0">
              <label className="text-sm text-gray-600 dark:text-gray-400 flex-shrink-0">Filter by Production:</label>
              <select
                value={deptFilterProductionId}
                onChange={(e) => setDeptFilterProductionId(e.target.value)}
                className="w-full md:w-auto min-w-0 max-w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-2 md:py-1 text-sm focus:ring-2 focus:ring-blue-500"
                data-testid="dept-filter-production"
              >
                <option value="">All Productions</option>
                {productions.map(p => (
                  <option key={p.id} value={p.id}>{p.company_name} - {p.production_name}</option>
                ))}
              </select>
            </div>
            {!showAddDepartment && editingDepartmentId === null && (
              <button
                // The convenience pre-fill carries the FILTER's value into the CREATE
                // form, and the filter deliberately lists every label the caller can
                // see — so it can seed a label they may not attach to. The select
                // would then render blank while the state still held that id, and
                // Create would post it and come back `production_not_yours`: the same
                // defect through a second door. Seed only from the labels the create
                // form actually offers.
                onClick={() => {
                  const seedable = deptFormProductions.some(p => String(p.id) === String(deptFilterProductionId));
                  setShowAddDepartment(true);
                  setDeptFormData({ name: '', production_id: seedable ? deptFilterProductionId : '' });
                }}
                className={BTN.primary}
                data-testid="add-department-btn"
              >
                + Add Department
              </button>
            )}
          </div>

          {/* The admin "all departments" list excludes personal "My Space"
              departments by default (owner ruling 2026-09-24) — same
              default-OFF, no-persistence toggle shape as "Show personal
              workspaces" on the Productions section above. Admin-only: a
              non-admin's list is already scoped to their own assignments,
              never "every user's" workspace, so the toggle has nothing to
              do there. */}
          {isCurrentUserAdmin && (
            <div className="mb-4">
              <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showPersonalDepartments}
                  onChange={(e) => setShowPersonalDepartments(e.target.checked)}
                  className="rounded"
                  data-testid="show-personal-departments-toggle"
                />
                Show personal workspaces
              </label>
            </div>
          )}

          {/* Add Department Form (Edit form is rendered inline below the dept row being edited) */}
          {showAddDepartment && <div className="mb-4">{departmentForm}</div>}

          {departmentsLoading ? (
            <p className="text-gray-500 text-sm">Loading departments...</p>
          ) : departments.length === 0 ? (
            <p className="text-gray-500 text-sm">No departments found. Create one to get started.</p>
          ) : (
            <div className="space-y-2">
              {departments.map(dept => (
                <div key={dept.id} className="border border-gray-200 rounded-lg [&>*:first-child]:rounded-t-lg [&>*:last-child]:rounded-b-lg" data-testid={`dept-${dept.id}`}>
                  {/* No overflow-hidden: it clipped the Add-member search dropdown when the card
                     was short (owner 2026-11-02). The rounded corners come from the first and last
                     child instead, so the header/panel backgrounds still follow the border. */}
                  <div
                    className="flex flex-wrap items-center justify-between gap-2 p-3 bg-white hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      // If editing this dept, one click closes both edit form and members list
                      if (editingDepartmentId === dept.id) {
                        handleCancelDeptEdit();
                        if (expandedDeptId === dept.id) handleToggleDeptExpand(dept.id);
                      } else {
                        handleToggleDeptExpand(dept.id);
                      }
                    }}
                  >
                    <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full md:w-auto md:flex-1 min-w-0">
                      <span data-chevron className={`text-xs text-gray-400 transition-transform duration-200 flex-shrink-0 ${expandedDeptId === dept.id ? 'rotate-90' : ''}`}>&#9654;</span>
                      <div className="min-w-0">
                        <span className="font-medium text-gray-800">{dept.name}</span>
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {dept.member_count || 0} member{dept.member_count !== 1 ? 's' : ''}
                        </span>
                        <span className="ml-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {dept.sub_department_count || 0} sub-dept{dept.sub_department_count !== 1 ? 's' : ''}
                        </span>
                        <span className="block md:inline md:ml-2 text-xs text-gray-500 truncate">
                          {dept.company_name} - {dept.production_name}
                        </span>
                      </div>
                    </div>
                    {/* ⚠️ EDIT AND DELETE ARE GATED DIFFERENTLY, AND THAT IS THE
                        WHOLE POINT — DO NOT COLLAPSE THEM BACK (2026-08-10).
                        `PUT /api/departments/:id` was widened on 2026-08-08 to
                        site admin OR `canManageDepartmentSettings` IN THIS
                        DEPARTMENT ("Site admin should not be needed for any day
                        to day running"), but this block stayed `isCurrentUserAdmin`
                        for both buttons, so that widening was UNREACHABLE in the
                        UI: nobody below site admin could rename their own
                        department. (`DELETE` was `isAdmin(req)` until 2026-10-08; Delete now
                        follows the row's server-computed `can_delete`, below.)

                        `capabilitiesFor(dept.id)` is the SERVER's per-department
                        answer, carried on the assignment rows by
                        GET /api/roles/my-assignments — a lookup, not a
                        re-derivation, and it is the right question here because
                        unlike a production label a department IS the box whose
                        settings are being asked about. A non-admin's list only
                        ever contains departments they are assigned to, so the
                        lookup always has an answer; a department with no
                        assignment returns NO_CAPABILITIES, i.e. false. */}
                    {(isCurrentUserAdmin || capabilitiesFor(dept.id).canManageDepartmentSettings === true) && (
                    <div className="flex gap-2 flex-shrink-0 w-full md:w-auto md:gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleEditDepartment(dept)}
                        className={`${BTN_XS.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center flex-1 md:flex-none`}
                        data-testid={`edit-dept-${dept.id}`}
                      >
                        Edit
                      </button>
                      {/* Delete renders from the SERVER's `can_delete` (owner
                          2026-10-08: a settings holder may delete a department
                          with one member or none; site admin any; never one
                          with orders) — never re-derived here. */}
                      {dept.can_delete === true && (
                      <button
                        onClick={() => setPendingDelete({ kind: 'department', item: dept })}
                        className={`${BTN_XS['danger-soft']} min-h-[44px] md:min-h-0 inline-flex items-center justify-center flex-1 md:flex-none`}
                        data-testid={`delete-dept-${dept.id}`}
                      >
                        Delete
                      </button>
                      )}
                    </div>
                    )}
                  </div>

                  {/* Inline edit form — appears directly below the row being edited */}
                  {editingDepartmentId === dept.id && (
                    <div className="border-t-2 border-blue-200 dark:border-blue-700 bg-blue-50/40 dark:bg-blue-900/10 p-3">
                      {departmentForm}
                    </div>
                  )}

                  {/* Expanded: Members list */}
                  {expandedDeptId === dept.id && (
                    <div className="border-t border-gray-200 bg-gray-50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="md:hidden text-xs font-semibold text-gray-600 uppercase">Members</h5>
                        <div className="hidden md:block"></div>
                        {addingMemberDeptId !== dept.id
                          && (isCurrentUserAdmin || capabilitiesFor(dept.id)?.canManageDepartmentSettings === true) && (
                          <button
                            onClick={() => setAddingMemberDeptId(dept.id)}
                            className={`${BTN_XS.primary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
                            data-testid={`add-member-${dept.id}`}
                          >
                            + Add person
                          </button>
                        )}
                      </div>

                      {/* Desktop-only column headers — aligned with the data row grid below */}
                      <div className="hidden md:grid items-center gap-3 px-3 mb-1" style={{ gridTemplateColumns: '2fr 1.5fr 1.5fr 40px' }}>
                        <h5 className="text-xs font-semibold text-gray-600 uppercase">Member Name</h5>
                        <h5 className="text-xs font-semibold text-gray-600 uppercase">Sub Department</h5>
                        <h5 className="text-xs font-semibold text-gray-600 uppercase">Access Rights</h5>
                        <span></span>
                      </div>

                      {/* The one "+ Add person" box (2026-11-03): people you work with, or
                          "+ Invite …" as the dropdown's last row — AddPersonPanel.js. */}
                      {addingMemberDeptId === dept.id && (
                        <AddPersonPanel
                          dept={dept}
                          isGlobalAdmin={!!isCurrentUserAdmin}
                          activeDepartment={activeDepartment}
                          canAddExisting
                          canSendInvite={canChangeMemberRole(dept.id)}
                          canAddLater={!!isCurrentUserAdmin || capabilitiesFor(dept.id)?.canManageDepartmentSettings === true}
                          subDepartments={subDeptsByDept[dept.id] || []}
                          excludeUserIds={(deptMembers[dept.id] || []).map(m => m.user_id).filter(Boolean)}
                          onClose={() => setAddingMemberDeptId(null)}
                          onDone={(message, tone) => {
                            // The box STAYS OPEN for the next person (owner 2026-11-03:
                            // "allowing me to add more people"), and the refresh is
                            // quiet — a spinner would collapse every card.
                            setSuccessTone(tone === 'warning' ? 'warning' : 'success');
                            setSuccessMessage(message);
                            setTimeout(() => { setSuccessMessage(''); setSuccessTone('success'); }, 6000);
                            fetchDeptMembers(dept.id);
                            fetchDepartments(undefined, undefined, { quiet: true });
                          }}
                        />
                      )}

                      {/* Members list */}
                      {!deptMembers[dept.id] ? (
                        <p className="text-xs text-gray-400">Loading members...</p>
                      ) : deptMembers[dept.id].length === 0 ? (
                        <p className="text-xs text-gray-400">No members assigned yet.</p>
                      ) : (
                        <div className="space-y-1">
                          {deptMembers[dept.id].map(member => {
                            const roleColors = {
                              buyer: 'bg-purple-100 text-purple-700',
                              supervisor: 'bg-teal-100 text-teal-700',
                              sub_department_lead: 'bg-amber-100 text-amber-700',
                              user: 'bg-gray-100 text-gray-600'
                            };
                            return (
                              <React.Fragment key={member.id}>
                                {/* Mobile card layout — long-press to remove */}
                                <div
                                  className="md:hidden bg-white rounded border border-gray-200 dark:border-gray-700 p-3 flex flex-col gap-2 select-none"
                                  onTouchStart={() => startLongPress(dept.id, member)}
                                  onTouchEnd={cancelLongPress}
                                  onTouchMove={cancelLongPress}
                                  onTouchCancel={cancelLongPress}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="flex-shrink-0"><UserStatusIndicator status={member.status} /></span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{member.username}</span>
                                    <MemberInviteButton member={member} deptId={dept.id} testIdSuffix="-m"
                                      show={!!isCurrentUserAdmin || capabilitiesFor(dept.id)?.canManageDepartmentSettings === true}
                                      onDone={({ message, tone }) => {
                                        setSuccessTone(tone); setSuccessMessage(message);
                                        setTimeout(() => { setSuccessMessage(''); setSuccessTone('success'); }, 6000);
                                        fetchDeptMembers(dept.id);
                                      }} />
                                  </div>
                                  {member.user_id && (
                                    <div>
                                      <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Sub Department</label>
                                      <select
                                        value={member.sub_department_id || ''}
                                        onChange={(e) => handleUpdateMemberSubDept(dept.id, member.user_id, e.target.value)}
                                        onTouchStart={(e) => { e.stopPropagation(); cancelLongPress(); }}
                                        className="min-h-[44px] w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                                        data-testid={`member-subdept-mobile-${member.username}`}
                                      >
                                        <option value="">&mdash; None &mdash;</option>
                                        {(subDeptsByDept[dept.id] || []).map(sd => (
                                          <option key={sd.id} value={sd.id}>{sd.name}</option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                  <div>
                                    <label className="block text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Access Rights</label>
                                    {canChangeMemberRole(dept.id) ? (
                                    <select
                                      value={member.role}
                                      onChange={(e) => handleUpdateMemberRole(dept.id, member.user_id, e.target.value)}
                                      onTouchStart={(e) => { e.stopPropagation(); cancelLongPress(); }}
                                      className="min-h-[44px] w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                                      aria-describedby={member.last_manager_locked ? `member-role-lock-mobile-${member.id}` : undefined}
                                      data-testid={`member-role-mobile-${member.username}`}
                                    >
                                      {MEMBER_ROLE_OPTIONS.map(([value, label]) => (
                                        <option key={value} value={value} disabled={lastManagerBlocksRole(member, value)}>{label}</option>
                                      ))}
                                    </select>
                                    ) : (
                                      <p className="text-sm text-gray-700 dark:text-gray-300 py-1"
                                        data-testid={`member-role-mobile-readonly-${member.username}`}>
                                        {ROLE_LABELS[member.role] || member.role}
                                      </p>
                                    )}
                                    {member.last_manager_locked && canChangeMemberRole(dept.id) && (
                                      <p id={`member-role-lock-mobile-${member.id}`} className="mt-1 text-xs text-gray-500 dark:text-gray-400"
                                        data-testid={`member-role-lock-mobile-${member.username}`}>
                                        {LAST_MANAGER_REASON}
                                      </p>
                                    )}
                                  </div>
                                  <p className="text-[10px] text-gray-400 italic">Long-press to remove</p>
                                </div>

                                {/* Desktop row layout — column-aligned: name | sub-dept | role | remove (matches column headers above) */}
                                <div className="hidden md:grid items-center gap-3 bg-white rounded px-3 py-1.5 text-sm" style={{ gridTemplateColumns: '2fr 1.5fr 1.5fr 40px' }}>
                                  <span className="flex items-center gap-1.5 min-w-0">
                                    <span className="flex-shrink-0"><UserStatusIndicator status={member.status} /></span>
                                    <span className="text-gray-800 truncate min-w-0">{member.username}</span>
                                    <MemberInviteButton member={member} deptId={dept.id}
                                      show={!!isCurrentUserAdmin || capabilitiesFor(dept.id)?.canManageDepartmentSettings === true}
                                      onDone={({ message, tone }) => {
                                        setSuccessTone(tone); setSuccessMessage(message);
                                        setTimeout(() => { setSuccessMessage(''); setSuccessTone('success'); }, 6000);
                                        fetchDeptMembers(dept.id);
                                      }} />
                                  </span>
                                  {member.user_id ? (
                                    <select
                                      value={member.sub_department_id || ''}
                                      onChange={(e) => handleUpdateMemberSubDept(dept.id, member.user_id, e.target.value)}
                                      className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-pointer min-w-0 truncate"
                                      data-testid={`member-subdept-${member.username}`}
                                    >
                                      <option value="">&mdash; None &mdash;</option>
                                      {(subDeptsByDept[dept.id] || []).map(sd => (
                                        <option key={sd.id} value={sd.id}>{sd.name}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <span className="text-xs text-gray-500 truncate min-w-0" title="Ghost row — delete this orphan and re-add via + Add person">
                                      {member.sub_department_name || <span className="text-gray-400 dark:text-gray-500">&mdash;</span>}
                                    </span>
                                  )}
                                  {canChangeMemberRole(dept.id) ? (
                                  // 2026-10-08 (owner): the EDITABLE select wears the
                                  // Sub Department select's bordered treatment beside it;
                                  // the colour pill is kept for the READ-ONLY state only,
                                  // so editable and static are distinguishable at a glance.
                                  <select
                                    value={member.role}
                                    onChange={(e) => handleUpdateMemberRole(dept.id, member.user_id, e.target.value)}
                                    title={member.last_manager_locked ? LAST_MANAGER_REASON : undefined}
                                    className="text-xs px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-pointer min-w-0 truncate"
                                    data-testid={`member-role-${member.username}`}
                                  >
                                    {MEMBER_ROLE_OPTIONS.map(([value, label]) => (
                                      <option key={value} value={value} disabled={lastManagerBlocksRole(member, value)}>{label}</option>
                                    ))}
                                  </select>
                                  ) : (
                                    // Same pill, no control: the viewer may not change it here.
                                    <span className={`justify-self-start text-xs px-2 py-0.5 rounded-full ${roleColors[member.role] || 'bg-gray-100 text-gray-600'}`}
                                      data-testid={`member-role-readonly-${member.username}`}>
                                      {ROLE_LABELS[member.role] || member.role}
                                    </span>
                                  )}
                                  {/* Removing a member is admin-only (server-enforced); hide for buyers. */}
                                  {isCurrentUserAdmin && (
                                  <button
                                    onClick={() => handleRemoveDeptMember(dept.id, member)}
                                    className={BTN_XS['danger-soft']}
                                    title="Remove member"
                                    data-testid={`remove-member-${member.username}`}
                                  >
                                    &#10005;
                                  </button>
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <p className="text-xs text-gray-400 mt-2">{departments.length} department{departments.length !== 1 ? 's' : ''}</p>
            </div>
          )}
        </div>
      </CollapsibleSection>
      )}

      {/* Sub-Departments Section — deptSettings (was ungated on render) */}
      {visibleTabIds.has('settings-sub-departments') && (
      <CollapsibleSection phoneAccordion
        id="settings-sub-departments"
        title="Sub-Departments"
        className={`mt-6 ${activeTab === 'settings-sub-departments' ? '' : 'md:hidden'}`}
        forceExpanded={activeTab === 'settings-sub-departments'}
        onToggle={(nextCollapsed) => {
          if (nextCollapsed) {
            setAddingSubDeptForDeptId(null);
            setEditingSubDeptId(null);
            setConfirmDeleteSubDeptId(null);
            setSubDeptError('');
          }
        }}
      >
        <div className="p-3 md:p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Sub-departments are teams within a department (e.g. <span className="font-medium text-gray-800 dark:text-gray-200">SFX &rarr; Snow team</span>). Add, rename, or remove them per department below.
          </p>

          {/* Production filter — only real productions (exclude personal "My Space"),
              and only shown when there is more than one to choose between. */}
          {(() => {
            const realProductions = productions.filter(p => !p.is_personal);
            if (realProductions.length <= 1) return null;
            return (
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2 mb-4">
                <label className="text-sm text-gray-600 dark:text-gray-400 flex-shrink-0">Filter by Production:</label>
                <select
                  value={subDeptProductionFilter}
                  onChange={(e) => setSubDeptProductionFilter(e.target.value)}
                  className="w-full md:w-auto min-w-0 max-w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded px-2 py-2 md:py-1 text-sm focus:ring-2 focus:ring-blue-500"
                  data-testid="subdept-filter-production"
                >
                  <option value="">All Productions</option>
                  {realProductions.map(p => (
                    <option key={p.id} value={p.id}>{p.company_name} - {p.production_name}</option>
                  ))}
                </select>
              </div>
            );
          })()}

          {subDeptError && (
            <div className="mb-3 px-3 py-2 rounded text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
              {subDeptError}
            </div>
          )}

          {departmentsLoading ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">Loading departments...</p>
          ) : (() => {
            const visibleDepts = departments.filter(d =>
              !d.is_personal &&
              (!subDeptProductionFilter || String(d.production_id) === String(subDeptProductionFilter))
            );
            if (visibleDepts.length === 0) {
              return <p className="text-gray-500 dark:text-gray-400 text-sm">No departments found.</p>;
            }
            return (
              <div className="space-y-4">
                {visibleDepts.map(dept => {
                  const subs = subDepartments
                    .filter(sd => sd.department_id === dept.id)
                    .sort((a, b) => a.name.localeCompare(b.name));
                  const pendingAddName = newSubDeptNameByDept[dept.id] || '';
                  return (
                    <div key={dept.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-testid={`subdept-group-${dept.id}`}>
                      {/* Department heading */}
                      <div className="flex flex-wrap items-center justify-between gap-2 p-3 bg-gray-50 dark:bg-slate-700/40 border-b border-gray-200 dark:border-gray-700">
                        <div className="min-w-0">
                          <span className="font-medium text-gray-800 dark:text-gray-100">{dept.name}</span>
                          <span className="ml-2 inline-flex items-center text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {subs.length} sub-dept{subs.length !== 1 ? 's' : ''}
                          </span>
                          <span className="block md:inline md:ml-2 text-xs text-gray-500 dark:text-gray-400 truncate">
                            {dept.company_name} - {dept.production_name}
                          </span>
                        </div>
                        {addingSubDeptForDeptId !== dept.id && (
                          <button
                            onClick={() => {
                              setAddingSubDeptForDeptId(dept.id);
                              setNewSubDeptNameByDept(prev => ({ ...prev, [dept.id]: '' }));
                              setEditingSubDeptId(null);
                              setConfirmDeleteSubDeptId(null);
                              setSubDeptError('');
                            }}
                            className={`${BTN_XS.primary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
                            data-testid={`add-subdept-${dept.id}`}
                          >
                            + Add Sub-Department
                          </button>
                        )}
                      </div>

                      {/* Department body */}
                      <div className="p-3 space-y-2 bg-white dark:bg-gray-800">
                        {/* Add form */}
                        {addingSubDeptForDeptId === dept.id && (
                          <div className="flex flex-col sm:flex-row gap-2 p-2 rounded border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10">
                            <input
                              type="text"
                              value={pendingAddName}
                              autoFocus
                              onChange={(e) => setNewSubDeptNameByDept(prev => ({ ...prev, [dept.id]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAddSubDepartment(dept.id);
                                if (e.key === 'Escape') { setAddingSubDeptForDeptId(null); setSubDeptError(''); }
                              }}
                              placeholder="e.g. Snow team"
                              className="flex-1 min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                              data-testid={`new-subdept-name-${dept.id}`}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAddSubDepartment(dept.id)}
                                disabled={addingSubDept || !pendingAddName.trim()}
                                className={`${BTN_XS.confirm} min-h-[44px] md:min-h-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
                                data-testid={`save-subdept-${dept.id}`}
                              >
                                {addingSubDept ? '...' : 'Add'}
                              </button>
                              <button
                                onClick={() => { setAddingSubDeptForDeptId(null); setSubDeptError(''); }}
                                className={`${BTN_XS.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Sub-department rows */}
                        {subs.length === 0 ? (
                          <p className="text-xs text-gray-400 dark:text-gray-500">No sub-departments yet.</p>
                        ) : subs.map(sd => (
                          <div key={sd.id} className="border border-gray-200 dark:border-gray-700 rounded" data-testid={`subdept-${sd.id}`}>
                            {editingSubDeptId === sd.id ? (
                              /* Inline edit (rename) */
                              <div className="flex flex-col sm:flex-row gap-2 p-2 bg-blue-50/40 dark:bg-blue-900/10">
                                <input
                                  type="text"
                                  value={editingSubDeptName}
                                  autoFocus
                                  onChange={(e) => setEditingSubDeptName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleRenameSubDepartment(sd.id);
                                    if (e.key === 'Escape') { setEditingSubDeptId(null); setSubDeptError(''); }
                                  }}
                                  className="flex-1 min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                                  data-testid={`edit-subdept-input-${sd.id}`}
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleRenameSubDepartment(sd.id)}
                                    disabled={savingSubDept || !editingSubDeptName.trim()}
                                    className={`${BTN_XS.confirm} min-h-[44px] md:min-h-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
                                    data-testid={`save-rename-subdept-${sd.id}`}
                                  >
                                    {savingSubDept ? '...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={() => { setEditingSubDeptId(null); setSubDeptError(''); }}
                                    className={`${BTN_XS.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : confirmDeleteSubDeptId === sd.id ? (
                              /* Delete confirmation with cascade warning */
                              <div className="p-2 bg-red-50 dark:bg-red-900/10 border-l-2 border-red-400 dark:border-red-600">
                                <p className="text-xs text-red-700 dark:text-red-300 mb-2">
                                  Delete <span className="font-semibold">&ldquo;{sd.name}&rdquo;</span>? This also removes any approval workflow scoped to this sub-department and unassigns its members. Existing orders keep their text label.
                                </p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleDeleteSubDepartment(sd.id)}
                                    className={`${BTN_XS.danger} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
                                    data-testid={`confirm-delete-subdept-${sd.id}`}
                                  >
                                    Delete
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeleteSubDeptId(null)}
                                    className={`${BTN_XS.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* Display row */
                              <div className="flex items-center justify-between gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                                  {sd.name}
                                  {/* The protected "Unassigned" (2026-10-28): the server refuses rename + delete for every caller, so neither control is offered. Keyed on the FLAG, never the name. */}
                                  {Number(sd.is_unassigned) === 1 && (
                                    <span
                                      className="ml-2 text-xs text-gray-500 dark:text-gray-400"
                                      title="Every department has this sub-department for people who are not in another one"
                                      data-testid={`subdept-builtin-${sd.id}`}
                                    >
                                      Built in, can&rsquo;t be renamed or deleted
                                    </span>
                                  )}
                                </span>
                                <div className="flex gap-2 flex-shrink-0 items-center">
                                  {/* Calculated overtime (2026-10-26): a shooting unit's crew are ON SET (camera O/T). Same route + gate as Rename. */}
                                  <label className="inline-flex min-h-[44px] md:min-h-0 items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer"
                                    title="Its crew count as on set: camera O/T is measured against this unit's call and wrap">
                                    <input
                                      type="checkbox"
                                      checked={Number(sd.is_shooting_unit) === 1}
                                      disabled={savingSubDept}
                                      onChange={(e) => handleToggleShootingUnit(sd.id, e.target.checked)}
                                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
                                      data-testid={`shooting-unit-${sd.id}`}
                                    />
                                    Shooting unit
                                  </label>
                                  {Number(sd.is_unassigned) !== 1 && (<>
                                  <button
                                    onClick={() => {
                                      setEditingSubDeptId(sd.id);
                                      setEditingSubDeptName(sd.name);
                                      setConfirmDeleteSubDeptId(null);
                                      setAddingSubDeptForDeptId(null);
                                      setSubDeptError('');
                                    }}
                                    className={`${BTN_XS.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
                                    data-testid={`edit-subdept-${sd.id}`}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => {
                                      setConfirmDeleteSubDeptId(sd.id);
                                      setEditingSubDeptId(null);
                                      setAddingSubDeptForDeptId(null);
                                      setSubDeptError('');
                                    }}
                                    className={`${BTN_XS['danger-soft']} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}
                                    data-testid={`delete-subdept-${sd.id}`}
                                  >
                                    Delete
                                  </button>
                                  </>)}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  {subDepartments.length} sub-department{subDepartments.length !== 1 ? 's' : ''} across {visibleDepts.length} department{visibleDepts.length !== 1 ? 's' : ''}
                </p>
              </div>
            );
          })()}
        </div>
      </CollapsibleSection>
      )}

      {/* Suppliers Section — deptSettings (was ungated on render) */}
      {visibleTabIds.has('settings-suppliers') && (
      <CollapsibleSection phoneAccordion
        id="settings-suppliers"
        title="Suppliers"
        className={`mt-6 ${activeTab === 'settings-suppliers' ? '' : 'md:hidden'}`}
        forceExpanded={activeTab === 'settings-suppliers'}
        headerActions={<ColumnCustomizer columns={supplierColVis.columns} isVisible={supplierColVis.isVisible} toggleColumn={supplierColVis.toggleColumn} />}
        onToggle={(nextCollapsed) => { if (nextCollapsed) { setShowAddSupplier(false); setEditingSupplierId(null); setExpandedSupplierId(null); } }}
      >
        <div className="p-3 md:p-6">
          <div className="flex items-center justify-end mb-4">
            {!showAddSupplier && editingSupplierId === null && (
              <button
                onClick={() => { setShowAddSupplier(true); setEditingSupplierId(null); setSupplierFormData({ name: '', contact_name: '', phone: '', email: '', website: '', account_number: '' }); setError(null); }}
                className={BTN.primary}
              >
                + Add Supplier
              </button>
            )}
          </div>

          {/* Add Supplier Form (Edit form is rendered inline below the row being edited) */}
          {showAddSupplier && <div className="mb-6">{supplierForm}</div>}

        {/* Suppliers List */}
        {suppliersLoading ? (
          <p className="text-gray-500 text-sm">Loading suppliers...</p>
        ) : suppliers.length === 0 ? (
          <p className="text-gray-500 text-sm">No suppliers yet. Click "Add Supplier" to create one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-sm" style={{ tableLayout: 'fixed', minWidth: '100%', width: 'max-content' }}>
              <colgroup>
                {supplierColVis.isVisible('name') && <col style={{ width: supplierResize.getColWidth('name') }} />}
                {supplierColVis.isVisible('contact') && <col className="hidden sm:table-column" style={{ width: supplierResize.getColWidth('contact') }} />}
                {supplierColVis.isVisible('phone') && <col className="hidden sm:table-column" style={{ width: supplierResize.getColWidth('phone') }} />}
                {supplierColVis.isVisible('email') && <col className="hidden sm:table-column" style={{ width: supplierResize.getColWidth('email') }} />}
                {supplierColVis.isVisible('account') && <col className="hidden md:table-column" style={{ width: supplierResize.getColWidth('account') }} />}
                <col style={{ width: supplierResize.getColWidth('actions') }} />
              </colgroup>
              <thead>
                <tr className="border-b border-gray-200">
                  {supplierColVis.isVisible('name') && <ResizableHeader colKey="name" width={supplierResize.getColWidth('name')} onResizeStart={supplierResize.handleResizeStart} className="text-left py-2 px-3 font-semibold text-gray-600">Supplier Name</ResizableHeader>}
                  {supplierColVis.isVisible('contact') && <ResizableHeader colKey="contact" width={supplierResize.getColWidth('contact')} onResizeStart={supplierResize.handleResizeStart} className="text-left py-2 px-3 font-semibold text-gray-600 hidden sm:table-cell">Contact</ResizableHeader>}
                  {supplierColVis.isVisible('phone') && <ResizableHeader colKey="phone" width={supplierResize.getColWidth('phone')} onResizeStart={supplierResize.handleResizeStart} className="text-left py-2 px-3 font-semibold text-gray-600 hidden sm:table-cell">Phone</ResizableHeader>}
                  {supplierColVis.isVisible('email') && <ResizableHeader colKey="email" width={supplierResize.getColWidth('email')} onResizeStart={supplierResize.handleResizeStart} className="text-left py-2 px-3 font-semibold text-gray-600 hidden sm:table-cell">Email</ResizableHeader>}
                  {supplierColVis.isVisible('account') && <ResizableHeader colKey="account" width={supplierResize.getColWidth('account')} onResizeStart={supplierResize.handleResizeStart} className="text-left py-2 px-3 font-semibold text-gray-600 hidden md:table-cell">Account #</ResizableHeader>}
                  <th className="text-right py-2 px-3 font-semibold text-gray-600" style={{ width: supplierResize.getColWidth('actions') }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((supplier) => (
                  <React.Fragment key={supplier.id}>
                    <tr className={`border-b border-gray-100 hover:bg-gray-50 ${editingSupplierId === supplier.id ? 'bg-blue-50' : ''}`}>
                      {supplierColVis.isVisible('name') && <td className="py-2 px-3 text-gray-800">
                        <button
                          onClick={() => {
                            // If editing this supplier, one click closes both edit form and expanded view
                            if (editingSupplierId === supplier.id) {
                              handleCancelSupplierEdit();
                              setExpandedSupplierId(null);
                            } else {
                              // Not editing — toggle expanded view normally
                              setExpandedSupplierId(expandedSupplierId === supplier.id ? null : supplier.id);
                            }
                          }}
                          // 📱 44px tall below md (mobile batch item 19); `-my-2` eats the cell's padding so the row grows 37 → 44px, not 60.
                          className="text-left hover:text-blue-600 font-medium max-md:min-h-[44px] max-md:-my-2"
                        >
                          {supplier.name}
                          <span className="ml-1 text-xs text-gray-400">{expandedSupplierId === supplier.id ? '\u25B2' : '\u25BC'}</span>
                        </button>
                      </td>}
                      {supplierColVis.isVisible('contact') && <td className="py-2 px-3 text-gray-600 hidden sm:table-cell">{supplier.contact_name || <span className="text-gray-400 dark:text-gray-500">&mdash;</span>}</td>}
                      {supplierColVis.isVisible('phone') && <td className="py-2 px-3 text-gray-600 hidden sm:table-cell">
                        {supplier.phone ? (
                          <a href={`tel:${supplier.phone}`} className="text-blue-600 hover:text-blue-800 hover:underline">{supplier.phone}</a>
                        ) : <span className="text-gray-400 dark:text-gray-500">&mdash;</span>}
                      </td>}
                      {supplierColVis.isVisible('email') && <td className="py-2 px-3 text-gray-600 hidden sm:table-cell">
                        {supplier.email ? (
                          <a href={`mailto:${supplier.email}`} className="text-blue-600 hover:text-blue-800 hover:underline">{supplier.email}</a>
                        ) : <span className="text-gray-400 dark:text-gray-500">&mdash;</span>}
                      </td>}
                      {supplierColVis.isVisible('account') && <td className="py-2 px-3 text-gray-600 hidden md:table-cell">{supplier.account_number || <span className="text-gray-400 dark:text-gray-500">&mdash;</span>}</td>}
                      <td className="py-2 px-1 md:px-3 text-right">
                        <div className="flex gap-2 md:gap-1 w-full md:w-auto justify-end">
                          <button
                            onClick={() => handleEditSupplier(supplier)}
                            className={`${BTN_XS.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center flex-1 md:flex-none`}
                            data-testid={`edit-supplier-${supplier.id}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSupplier(supplier)}
                            disabled={deletingSupplier === supplier.id}
                            className={`${BTN_XS['danger-soft']} min-h-[44px] md:min-h-0 inline-flex items-center justify-center flex-1 md:flex-none`}
                            data-testid={`delete-supplier-${supplier.id}`}
                          >
                            {deletingSupplier === supplier.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Inline edit form — appears directly below the row being edited */}
                    {editingSupplierId === supplier.id && (
                      <tr>
                        <td colSpan="6" className="p-0">
                          <div className="p-2 md:p-3 bg-blue-50/40 dark:bg-blue-900/10 border-y-2 border-blue-200 dark:border-blue-700">
                            {supplierForm}
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedSupplierId === supplier.id && (
                      <tr className="bg-gray-50">
                        <td colSpan="6" className="px-3 py-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                            <div>
                              <p className="text-xs font-medium text-gray-500">Contact Name</p>
                              <p className="text-gray-800">{supplier.contact_name || <span className="text-gray-500 dark:text-gray-400">Not set</span>}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Phone</p>
                              {supplier.phone ? (
                                <a href={`tel:${supplier.phone}`} className="text-blue-600 hover:underline">{supplier.phone}</a>
                              ) : <p className="text-gray-500 dark:text-gray-400">Not set</p>}
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Email</p>
                              {supplier.email ? (
                                <a href={`mailto:${supplier.email}`} className="text-blue-600 hover:underline">{supplier.email}</a>
                              ) : <p className="text-gray-500 dark:text-gray-400">Not set</p>}
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Website</p>
                              {supplier.website ? (
                                <a href={supplier.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{supplier.website}</a>
                              ) : <p className="text-gray-500 dark:text-gray-400">Not set</p>}
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-500">Account Number</p>
                              <p className="text-gray-800">{supplier.account_number || <span className="text-gray-500 dark:text-gray-400">Not set</span>}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </CollapsibleSection>
      )}
      {/* Invite Users section removed — invite flow is now integrated into the Users section via PeopleManager */}

      {/* Users Section (unified — powered by users table) — deptSettings (was
          ungated on render). The anchor wrapper is gated too, so the whole block
          leaves the DOM rather than leaving an empty scroll target behind. */}
      {visibleTabIds.has('settings-users') && (
      <div id="users" ref={usersSectionRef}>
      <CollapsibleSection phoneAccordion id="settings-users" title="Users" className={`mt-6 ${activeTab === 'settings-users' ? '' : 'md:hidden'}`} forceExpanded={activeTab === 'settings-users'}>
        <PeopleManager />
      </CollapsibleSection>
      </div>
      )}

      {/* Roles Section — deptSettings since 2026-08-04. The authoring controls
          inside are separately gated on canAssignDepartmentRoles, and no
          non-admin may edit a SYSTEM role's permissions. */}
      {visibleTabIds.has('settings-roles') && (
      /* No ColumnCustomizer gear on this section — owner ruling 2026-08-15,
         Roles table only (Productions and Suppliers keep theirs). The columns
         below render UNCONDITIONALLY on purpose: when the gear was removed,
         any roleColVis.isVisible() wrappers left behind would strand a user
         who had hidden columns via the old gear — hidden forever with no
         control to restore them (state persisted in localStorage
         `sfx_hiddenCols_roles`, now orphaned and harmless). Do not re-wrap
         these columns in a visibility hook without restoring a control. */
      <CollapsibleSection phoneAccordion id="settings-roles" title="Roles & Access Rights" className={`mt-6 ${activeTab === 'settings-roles' ? '' : 'md:hidden'}`} forceExpanded={activeTab === 'settings-roles'}>
        <div className="p-4 sm:p-6">
          {roleError && (
            <div className="p-2 mb-3 bg-red-50 text-red-700 border border-red-200 rounded text-sm">
              {roleError}
            </div>
          )}
          {roleSuccess && (
            <div className="p-2 mb-3 bg-green-50 text-green-700 border border-green-200 rounded text-sm">
              {roleSuccess}
            </div>
          )}
          <p className="text-sm text-gray-500 mb-4">Define roles and their access rights. Assign roles to users in the Users section above.</p>

          {/* Mobile placeholder — Roles table has 14 columns (horizontal shortened permission labels since 2026-08-15), not viable on a phone. Use desktop to edit. */}
          <div className="md:hidden bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 text-center">
            <svg className="w-10 h-10 mx-auto text-amber-500 dark:text-amber-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Roles editor not available on mobile</p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">This table has 14 permission columns and is best edited on a desktop or tablet. Switch to a larger screen to view and edit roles.</p>
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="text-sm" style={{ tableLayout: 'fixed', minWidth: '100%', width: 'max-content' }}>
              <thead>
                <tr className="border-b border-gray-200">
                  <ResizableHeader colKey="role_name" width={roleResize.getColWidth('role_name')} onResizeStart={roleResize.handleResizeStart} className="text-left py-2 px-3 font-semibold text-gray-600">Role Name</ResizableHeader>
                  <ResizableHeader colKey="type" width={roleResize.getColWidth('type')} onResizeStart={roleResize.handleResizeStart} className="text-center py-2 px-3 font-semibold text-gray-600">Type</ResizableHeader>
                  {Object.entries(PERMISSION_LABELS).map(([perm, label]) => (
                    /* HORIZONTAL headers with shortened labels — owner ruling
                       2026-08-15, superseding the vertical rotated labels
                       (which also carried a Safari bug: WebKit ignores
                       writing-mode on a table-cell but still applies the
                       transform, rendering them horizontal AND upside-down).
                       Header row 192.61px -> 36.5px, measured in Chromium.
                       Three parts, all load-bearing:
                       - title on the <th> carries the FULL label — the hover
                         affordance that pays for the abbreviation;
                       - the visible short label is aria-hidden and the
                         sr-only span carries the full label, so assistive
                         tech announces the same name the tooltip shows (the
                         InactiveBadge device: title on a generic element is
                         not reliably exposed, so it is never the only
                         non-visual channel);
                       - no whitespace-nowrap: a long heading may wrap to a
                         natural second line rather than forcing column width.
                       No writingMode/transform anywhere in this row — pinned
                       by AdminSettings.rolesTableHeaders.test.js. No dark:
                       pair on text-gray-600: index.css's dark !important
                       override table maps it, a local pair would be inert. */
                    <th key={perm} className="text-center py-2 px-1 font-semibold text-gray-600 text-sm" title={label}>
                      <span aria-hidden="true">{PERMISSION_LABELS_SHORT[perm]}</span>
                      <span className="sr-only">{label}</span>
                    </th>
                  ))}
                  <th className="text-center py-2 px-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roleDefinitions.map(role => (
                  editingRoleId === role.id ? (
                    <tr key={role.id} className="border-b border-gray-100 bg-blue-50">
                      <td className="py-2 px-3">
                        <input
                          type="text"
                          value={roleFormData.display_name || ''}
                          onChange={(e) => setRoleFormData({...roleFormData, display_name: e.target.value})}
                          className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded px-2 py-1 text-sm"
                          data-testid="edit-role-name"
                        />
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${role.is_system ? 'bg-gray-200 text-gray-600' : 'bg-purple-100 text-purple-700'}`}>
                          {role.is_system ? 'System' : 'Custom'}
                        </span>
                      </td>
                      {Object.keys(PERMISSION_LABELS).map(perm => {
                        // NO non-admin edits permissions on a system role. Was
                        // `&& isCurrentUserBuyer`, which would have handed a
                        // supervisor MORE than a buyer the moment this section
                        // stopped being admin-only (2026-08-04).
                        const readOnly = !isCurrentUserAdmin && role.is_system;
                        return (
                          <td key={perm} className="py-2 px-1 text-center">
                            <input
                              type="checkbox"
                              checked={!!roleFormData[perm]}
                              onChange={(e) => !readOnly && setRoleFormData({...roleFormData, [perm]: e.target.checked})}
                              disabled={readOnly}
                              className={`w-4 h-4 border-gray-300 rounded focus:ring-blue-500 ${readOnly ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600'}`}
                              data-testid={`edit-role-${perm}`}
                            />
                          </td>
                        );
                      })}
                      <td className="py-2 px-3 text-center space-x-2">
                        <button
                          onClick={() => handleSaveRole(role.id)}
                          disabled={savingRole}
                          className={BTN_XS.confirm}
                          data-testid="save-role-btn"
                        >
                          {savingRole ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingRoleId(null)}
                          className={BTN_XS.secondary}
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={role.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 font-medium text-gray-800">{role.display_name}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${role.is_system ? 'bg-gray-200 text-gray-600' : 'bg-purple-100 text-purple-700'}`}>
                          {role.is_system ? 'System' : 'Custom'}
                        </span>
                      </td>
                      {Object.keys(PERMISSION_LABELS).map(perm => (
                        <td key={perm} className="py-2 px-1 text-center">
                          {role[perm] ? (
                            <span className="text-green-600 font-bold">✓</span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                      ))}
                      <td className="py-2 px-3 text-center space-x-2">
                        {/* Admins can edit all roles; a department role author
                            (buyer, or anyone holding canAssignDepartmentRoles
                            here) can only edit custom ones. */}
                        {(isCurrentUserAdmin || (canAssignDeptRoles && !role.is_system)) && (
                          <button
                            onClick={() => {
                              setEditingRoleId(role.id);
                              setRoleFormData({
                                display_name: role.display_name,
                                can_create_orders: !!role.can_create_orders,
                                can_view_own_orders: !!role.can_view_own_orders,
                                can_view_all_orders: !!role.can_view_all_orders,
                                can_approve_orders: !!role.can_approve_orders,
                                can_view_admin_dashboard: !!role.can_view_admin_dashboard,
                                can_manage_settings: !!role.can_manage_settings,
                                can_view_department_orders: !!role.can_view_department_orders,
                                can_view_department_dashboard: !!role.can_view_department_dashboard,
                                can_manage_roles: !!role.can_manage_roles,
                              });
                            }}
                            className={BTN_XS.secondary}
                            title="Edit role permissions"
                            data-testid={`edit-role-${role.id}`}
                          >
                            Edit
                          </button>
                        )}
                        {!role.is_system && (isCurrentUserAdmin || canAssignDeptRoles) && (
                          <button
                            onClick={() => handleDeleteRole(role.id)}
                            className={BTN_XS['danger-soft']}
                            title="Delete role"
                            data-testid={`delete-role-${role.id}`}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>

          {/* Add New Role — authoring is the `canAssignDepartmentRoles` half of
              the split, so a caller who can administer the department's settings
              but was not granted role authoring sees the table and no author
              control. Ungated before 2026-08-04 only because the whole section
              was admin-only. */}
          {!(isCurrentUserAdmin || canAssignDeptRoles) ? null : showAddRole ? (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Create New Role</h4>
              <div className="space-y-3">
                <div>
                  <label className={FIELD_LABEL_CLASS}>Role Name</label>
                  <input
                    type="text"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    placeholder="e.g. Senior Technician"
                    className="w-full max-w-sm border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="new-role-name"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateRole(); } }}
                  />
                </div>
                <div>
                  <label className={FIELD_LABEL_CLASS}>Access Rights</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(PERMISSION_LABELS).map(([perm, label]) => (
                      <label key={perm} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!newRolePermissions[perm]}
                          onChange={(e) => setNewRolePermissions({...newRolePermissions, [perm]: e.target.checked})}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          data-testid={`new-role-${perm}`}
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleCreateRole}
                    disabled={savingRole || !newRoleName.trim()}
                    className={BTN.confirm}
                    data-testid="create-role-btn"
                  >
                    {savingRole ? 'Creating...' : 'Create Role'}
                  </button>
                  <button
                    onClick={() => { setShowAddRole(false); setNewRoleName(''); setRoleError(null); }}
                    className={BTN_XS.secondary}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddRole(true)}
              className={`${BTN.primary} mt-4`}
              data-testid="add-role-btn"
            >
              + Add New Role
            </button>
          )}
        </div>
      </CollapsibleSection>
      )}

      {/* Approval Workflows Section — deptSettings (was ungated on render) */}
      {visibleTabIds.has('settings-workflows') && (
      <CollapsibleSection phoneAccordion
        id="settings-workflows"
        title="Approval Workflows"
        className={`mt-6 ${activeTab === 'settings-workflows' ? '' : 'md:hidden'}`}
        forceExpanded={activeTab === 'settings-workflows'}
        onToggle={(nextCollapsed) => setWorkflowsSectionCollapsed(nextCollapsed)}
      >
        <div className="p-3 md:p-6">
          <ApprovalWorkflowManager sectionCollapsed={workflowsSectionCollapsed} />
        </div>
      </CollapsibleSection>
      )}

      {/* Equipment Categories Section — adminOnly (already gated; moved onto the
          shared predicate so all nine sections read the same way) */}
      {visibleTabIds.has('settings-equipment-categories') && (
        <CollapsibleSection phoneAccordion id="settings-equipment-categories" title="Equipment Categories" className={`mt-6 ${activeTab === 'settings-equipment-categories' ? '' : 'md:hidden'}`} forceExpanded={activeTab === 'settings-equipment-categories'}>
          <EquipmentCategoryManager />
        </CollapsibleSection>
      )}

      {/* Backup & Restore Section — deptSettings since 2026-08-04. The
          WHOLE-INSTANCE half stays admin-only inside BackupRestore. */}
      {visibleTabIds.has('settings-backup') && (
      <CollapsibleSection phoneAccordion id="settings-backup" title="Backup & Restore" className={`mt-6 ${activeTab === 'settings-backup' ? '' : 'md:hidden'}`} forceExpanded={activeTab === 'settings-backup'}>
        <div className="p-4 sm:p-6">
          <BackupRestore isAdmin={isCurrentUserAdmin} activeDepartment={activeDepartment} />
        </div>
      </CollapsibleSection>
      )}
    </div>
  );
}

/**
 * TWO HALVES, AND ONLY ONE OF THEM IS SITE-WIDE.
 *
 * - "Full System Backup" renders behind `{isAdmin && …}` — whole-instance
 *   backup/restore is admin-only by construction here, and independently on the
 *   server (`/api/backup`, `/api/backup/restore` are `req.isGlobalAdmin` only).
 *   The 2026-08-04 ruling widened DEPARTMENT control, not site-wide admin.
 * - The DEPARTMENT half is gated on `deptId`, which for a non-admin resolves
 *   from `activeDepartment` — their own assignment, chosen by the department
 *   dropdown. It was never gated on a role, so a supervisor reaching this tab
 *   sees the department half without further change. There is deliberately no
 *   `isBuyer` prop: a role-name prop here is how the department half would
 *   quietly stop working for the next role granted the capability.
 */
function BackupRestore({ isAdmin, activeDepartment }) {
  const [message, setMessage] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState(null);

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Admin: fetch all departments for the picker. `includePersonal=true`
  // unconditionally — this picker has no toggle of its own, and nothing
  // server-side blocks backing up/restoring a personal "My Space" department
  // (task-LEAKSPACES-report.md), so hiding them here by the new server
  // default would be an unannounced narrowing of what a site admin could
  // already back up before the MYSPACE change (2026-09-24).
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await apiGet('/api/departments?includePersonal=true');
        if (res.ok) {
          const data = await res.json();
          setDepartments(data);
          if (data.length > 0 && !selectedDeptId) setSelectedDeptId(data[0].id);
        }
      } catch (e) { /* ignore */ }
    })();
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve department name and ID for the department backup section
  const deptId = isAdmin ? selectedDeptId : activeDepartment?.department_id;
  const deptName = isAdmin
    ? (departments.find(d => d.id === selectedDeptId)?.name || 'Department')
    : (activeDepartment?.department_name || 'Department');

  return (
    <div className="space-y-6">
      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Full System Backup — admin only */}
      {isAdmin && (
        <BackupSection
          title="Full System Backup"
          description="Download a complete backup of the entire database and all uploaded files."
          warningText="This will replace ALL current data with the backup data."
          infoEndpoint="/api/backup/info"
          backupEndpoint="/api/backup"
          restoreEndpoint="/api/backup/restore"
          filenamePrefix="crewforge-full-backup"
          formatSize={formatSize}
          message={message}
          setMessage={setMessage}
          infoRenderer={(info) => (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
              <span>{info.orders} orders</span>
              <span>{info.productions} productions</span>
              <span>{info.suppliers} suppliers</span>
              <span>{info.uploaded_files} uploaded files</span>
              {info.database_size > 0 && <span>DB size: {formatSize(info.database_size)}</span>}
            </div>
          )}
        />
      )}

      {/* Department Backup — an admin picks any department; everyone else gets
          the one they are standing in. */}
      {isAdmin && departments.length > 0 && (
        <div className="flex items-center gap-3 px-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
            Department:
          </label>
          <select
            value={selectedDeptId || ''}
            onChange={(e) => setSelectedDeptId(Number(e.target.value))}
            // `min-w-0`: a <select>'s min-content width is its WIDEST option,
            // so without it one long "Department (Production)" name widened
            // the whole page on a phone (measured 2026-09-25). Pinned by
            // AdminSettings.backupDepartmentPicker.test.js.
            className={`${FORM_INPUT_FIELD} flex-1 min-w-0`}
          >
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}{d.production_name ? ` (${d.production_name})` : ''}</option>
            ))}
          </select>
        </div>
      )}
      {deptId && (
        <BackupSection
          key={deptId}
          title={`Department Backup — ${deptName}`}
          description={`Download a backup of all ${deptName} data: orders, budget, crew, workflows, and uploaded files.`}
          warningText={`This will replace all data for ${deptName} only. Other departments will not be affected.`}
          infoEndpoint="/api/backup/department/info"
          backupEndpoint="/api/backup/department"
          restoreEndpoint="/api/backup/department/restore"
          filenamePrefix={`crewforge-${deptName.replace(/\s+/g, '-')}-backup`}
          formatSize={formatSize}
          message={message}
          setMessage={setMessage}
          isDepartment
          departmentId={isAdmin ? deptId : null}
          infoRenderer={(info) => (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
              <span>{info.orders} orders</span>
              <span>{info.crew_members} crew members</span>
              <span>{info.cost_codes} cost codes</span>
              <span>{info.budget_versions} budget versions</span>
              <span>{info.sub_departments} sub-departments</span>
              <span>{info.uploaded_files} uploaded files</span>
            </div>
          )}
        />
      )}
    </div>
  );
}

/** Reusable backup/restore section for both full and department scopes */
function BackupSection({ title, description, warningText, infoEndpoint, backupEndpoint, restoreEndpoint, filenamePrefix, formatSize, message, setMessage, infoRenderer, isDepartment, departmentId }) {
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [info, setInfo] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  // When admin selects a specific department, override x-department-id header
  const deptHeaders = departmentId ? { 'x-department-id': String(departmentId) } : undefined;

  const fetchInfo = useCallback(async () => {
    try {
      const res = await apiFetch(infoEndpoint, { method: 'GET', headers: deptHeaders });
      if (res.ok) setInfo(await res.json());
    } catch (e) { /* ignore */ }
  }, [infoEndpoint, departmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const handleCreate = async () => {
    setCreating(true);
    setMessage(null);
    try {
      const res = await apiFetch(backupEndpoint, { method: 'GET', headers: deptHeaders });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Backup failed');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `${filenamePrefix}-${timestamp}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `${title} created and downloaded successfully.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCreating(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowRestoreConfirm(true);
      setMessage(null);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    setRestoring(true);
    setMessage(null);
    setShowRestoreConfirm(false);
    try {
      const formData = new FormData();
      formData.append('backup', selectedFile);
      const res = await apiFetch(restoreEndpoint, { method: 'POST', body: formData, headers: deptHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Restore failed');

      const orderCount = isDepartment ? (data.counts?.orders || 0) : (data.restored?.orders || 0);
      let msg = `Backup restored successfully! ${orderCount} orders recovered.`;
      if (data.warnings?.length > 0) {
        msg += ` Warnings: ${data.warnings.join('; ')}`;
      }
      msg += ' Please reload the page to see updated data.';
      setMessage({ type: 'success', text: msg });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchInfo();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setRestoring(false);
    }
  };

  const cancelRestore = () => {
    setShowRestoreConfirm(false);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const spinnerSvg = (
    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-3">{title}</h3>

      {/* Info summary */}
      {info && (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 font-medium">Current Data:</p>
          {infoRenderer(info)}
        </div>
      )}

      {/* Create */}
      <div className="mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{description}</p>
        <button onClick={handleCreate} disabled={creating} className={`${BTN.primary} flex items-center gap-2`}>
          {creating ? (<>{spinnerSvg} Creating Backup...</>) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Backup
            </>
          )}
        </button>
      </div>

      {/* Restore */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Restore from Backup</h4>
        <p className="text-sm text-amber-600 dark:text-amber-400 mb-3 font-medium">
          Warning: {warningText}
        </p>

        {!showRestoreConfirm ? (
          <label className="inline-flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Select Backup File (.zip)
            <input ref={fileInputRef} type="file" accept=".zip" onChange={handleFileSelect} className="hidden" />
          </label>
        ) : (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium mb-2">
              Are you sure you want to restore from this backup?
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mb-1">
              File: <span className="font-mono">{selectedFile?.name}</span> ({selectedFile ? formatSize(selectedFile.size) : ''})
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-3">
              {warningText}
            </p>
            <div className="flex gap-3">
              <button onClick={handleRestore} disabled={restoring} className={`${BTN.danger} flex items-center gap-2`}>
                {restoring ? (<>{spinnerSvg} Restoring...</>) : 'Yes, Restore Backup'}
              </button>
              <button onClick={cancelRestore} disabled={restoring} className={BTN.secondary}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Equipment Category Manager ──────────────────────────────────────
function EquipmentCategoryManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ name: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const fetchCategories = async () => {
    try {
      const res = await apiGet('/api/marketplace/categories/all');
      if (!res.ok) throw new Error('Failed to load categories');
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  const handleExpand = (id) => {
    if (expandedId === id) {
      // Collapse
      setClosingId(id);
      setTimeout(() => { setExpandedId(null); setClosingId(null); }, 250);
    } else {
      // Open
      if (expandedId) {
        setClosingId(expandedId);
        setTimeout(() => {
          setExpandedId(id);
          setClosingId(null);
          setOpeningId(id);
          setTimeout(() => setOpeningId(null), 50);
        }, 250);
      } else {
        setExpandedId(id);
        setOpeningId(id);
        setTimeout(() => setOpeningId(null), 50);
      }
      const cat = categories.find(c => c.id === id);
      if (cat) setForm({ name: cat.name, sort_order: cat.sort_order });
    }
  };

  const handleSave = async (id) => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (id === 'new') {
        await apiPost('/api/marketplace/categories', form);
        setShowAddForm(false);
      } else {
        await apiPut(`/api/marketplace/categories/${id}`, form);
        // Collapse with animation
        setClosingId(id);
        setTimeout(() => { setExpandedId(null); setClosingId(null); }, 250);
      }
      await fetchCategories();
    } catch (err) {
      setError(err.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat) => {
    try {
      if (cat.is_active) {
        await apiDelete(`/api/marketplace/categories/${cat.id}`);
      } else {
        await apiFetch(`/api/marketplace/categories/${cat.id}/reactivate`, { method: 'PATCH' });
      }
      await fetchCategories();
    } catch (err) {
      setError(err.message || 'Failed to update category');
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading categories...</div>;

  return (
    <div className="p-3 md:p-6">
      {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">{error}</div>}

      {/* Add button */}
      {!showAddForm && (
        <button
          onClick={() => { setShowAddForm(true); setForm({ name: '', sort_order: categories.length }); }}
          className={BTN.primary}
        >
          + Add Category
        </button>
      )}

      {/* Add form */}
      {showAddForm && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">New Category</h4>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Category name"
              className="flex-1 px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
              placeholder="Sort order"
              className="w-24 px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={() => handleSave('new')} disabled={saving || !form.name.trim()} className={BTN_XS.confirm}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowAddForm(false)} className={BTN_XS.secondary}>Cancel</button>
          </div>
        </div>
      )}

      {/* Category table */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <th className="py-2 px-3">Name</th>
              <th className="py-2 px-3 hidden md:table-cell">Listings</th>
              <th className="py-2 px-3 hidden md:table-cell">Sort</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3 w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {categories.map((cat) => {
              const isExpanded = expandedId === cat.id;
              const isFading = closingId === cat.id || openingId === cat.id;
              return (
                <React.Fragment key={cat.id}>
                  <tr
                    onClick={() => handleExpand(cat.id)}
                    className={`cursor-pointer transition-colors ${
                      isExpanded
                        ? '[&>td]:bg-gray-100 dark:[&>td]:bg-slate-700'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                    }`}
                  >
                    <td className="py-2 px-3 font-medium text-gray-900 dark:text-white">{cat.name}</td>
                    <td className="py-2 px-3 hidden md:table-cell text-gray-600 dark:text-gray-400">{cat.listing_count || 0}</td>
                    <td className="py-2 px-3 hidden md:table-cell text-gray-600 dark:text-gray-400">{cat.sort_order}</td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        cat.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </td>
                  </tr>
                  {(isExpanded || closingId === cat.id) && (
                    <tr>
                      <td colSpan="5" className="p-0">
                        <div className={`p-3 md:p-4 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-600 transition-opacity duration-200 ease-out ${isFading ? 'opacity-0' : 'opacity-100'}`}>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <div className="flex-1">
                              <label className={FIELD_LABEL_CLASS}>Name</label>
                              <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                                className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div className="w-24">
                              <label className={FIELD_LABEL_CLASS}>Sort Order</label>
                              <input
                                type="number"
                                value={form.sort_order}
                                onChange={(e) => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                                className="w-full px-3 py-2 text-base md:text-sm min-h-[44px] md:min-h-0 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <button onClick={() => handleSave(cat.id)} disabled={saving || !form.name.trim()} className={BTN_XS.confirm}>
                              {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button onClick={() => handleExpand(cat.id)} className={BTN_XS.secondary}>Cancel</button>
                            <button
                              onClick={() => handleToggleActive(cat)}
                              className={cat.is_active ? BTN_XS['danger-soft'] : BTN_XS.confirm}
                            >
                              {cat.is_active ? 'Deactivate' : 'Reactivate'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {categories.length === 0 && (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">No equipment categories yet.</p>
        )}
      </div>
    </div>
  );
}

export default AdminSettings;
